import FeTileLayoutEngine from './partials/services/FeTileLayoutEngine';
import { Utils } from './partials/utils/utils';
import DragTransform from './partials/interfaces/DragTransform';
import { HeightData } from './partials/types/HeightData';
import DragDropUIData from './partials/interfaces/DragDropUIData';
import Node from './partials/interfaces/Node';
import { CompactOptions } from './partials/types/CompactOptions';
import { ColumnOptions } from './partials/types/ColumnOptions';
import { FeTileLayoutElement } from './partials/types/FeTileLayoutElement';
import { EventHandler } from './partials/types/EventHandler';
import { ElementHandler } from './partials/types/ElementHandler';
import { NodesHandler } from './partials/types/NodesHandler';
import { DroppedHandler } from './partials/types/DroppedHandler';
import { EventHandlerCallback } from './partials/types/EventHandlerCallback';
import { AddRemoveFunction } from './partials/types/AddRemoveFunction';
import { SaveFunction } from './partials/types/SaveFunction';
import { RenderFunction } from './partials/types/RenderFunction';
import { ResizeToContentFunction } from './partials/types/ResizeToContentFunction';
import ItemHTMLElement from './partials/interfaces/ItemHTMLElement';
import FeTileLayoutOptions from './partials/interfaces/FeTileLayoutOptions';
import MoveOptions from './partials/interfaces/MoveOptions';
import PositionDimention from './partials/interfaces/PositionDimention';
import FeTileLayoutItem from './partials/interfaces/FeTileLayoutItem';
import DragDropDragInOptions from './partials/interfaces/DragDropDragInOptions';
import Position from './partials/interfaces/Position';
import DragDropTileLayout from './partials/services/DragDropTileLayout';
import { isTouch } from './partials/utils/touchUtils';
import DragDropManager from './partials/services/DragDropManager';
import DragDropElementHost from './partials/interfaces/DragDropElementHost';
import { NumberOrString } from './partials/types/NumberOrString';
const dd = new DragDropTileLayout;

// can add exports as needed outside of library
// export all dependent file as well to make it easier for users to just import the main file
export * from './partials/utils/utils';
export * from './partials/services/DragDropTileLayout';

export interface FeTileLayoutHTMLElement extends HTMLElement
{
  tileLayout?: FeTileLayout; // layout's parent DOM element points back to tile layout class
}

/** list of possible events, or space separated list of them */
export type FeTileLayoutEvent = 'added' | 'change' | 'disable' | 'drag' | 'dragstart' | 'dragstop' | 'dropped' |
  'enable' | 'removed' | 'resize' | 'resizestart' | 'resizestop' | 'resizecontent';

/** Defines the coordinates of an object */
export interface MousePosition
{
  top: number;
  left: number;
}

/** Defines the position of a cell inside the layout*/
export interface CellPosition
{
  x: number;
  y: number;
}

interface TileLayoutCSSStyleSheet extends CSSStyleSheet
{
  _max?: number; // internal tracker of the max # of rows we created
}

// extend with internal fields we need - TODO: move other items in here
interface InternalFeTileLayoutOptions extends FeTileLayoutOptions
{
  _alwaysShowResizeHandle?: true | false | 'mobile'; // so we can restore for save
}

// default values for tile layout options - used during init and when saving out
export const feTileLayoutDefaults: FeTileLayoutOptions = {
  alwaysShowResizeHandle: 'mobile',
  animate: true,
  auto: true,
  cellHeight: 'auto',
  cellHeightThrottle: 100,
  cellHeightUnit: 'px',
  column: 12,
  draggable: { handle: '.fe-tile-layout-item-content', appendTo: 'body', scroll: true },
  handle: '.fe-tile-layout-item-content',
  itemClass: 'fe-tile-layout-item',
  margin: 10,
  marginUnit: 'px',
  maxRow: 0,
  minRow: 0,
  placeholderClass: 'fe-tile-layout-placeholder',
  placeholderText: '',
  removableOptions: { accept: 'fe-tile-layout-item', decline: 'fe-tile-layout-non-removable' },
  resizable: { handles: 'se' },
  rtl: 'auto',

  // **** same as not being set ****
  // disableDrag: false,
  // disableResize: false,
  // float: false,
  // handleClass: null,
  // removable: false,
  // staticLayout: false,
  // styleInHead: false,
  //removable
};

/** default dragIn options */
export const dragInDefaultOptions: DragDropDragInOptions = {
  handle: '.fe-tile-layout-item-content',
  appendTo: 'body',
  // revert: 'invalid',
  // scroll: false,
};

/**
 * Main layout class - you will need to call `FeTileLayout.init()` first to initialize your layout.
 * Note: your tile layout elements MUST have the following classes for the CSS layout to work:
 * @example
 * <div class="fe-tile-layout">
 *   <div class="fe-tile-layout-item">
 *     <div class="fe-tile-layout-item-content">Item 1</div>
 *   </div>
 * </div>
 */
export class FeTileLayout
{

  /**
   * initializing the HTML element, or selector string, into a tile layout will return the layout. Calling it again will
   * simply return the existing instance (ignore any passed options). There is also an initAll() version that support
   * multiple layouts initialization at once. Or you can use addLayout() to create the entire tile layout from JSON.
   * @param options tile layout options (optional)
   * @param elOrString element or CSS selector (first one used) to convert to a tile layout (default to '.fe-tile-layout' class selector)
   *
   * @example
   * let tile layout = FeTileLayout.init();
   *
   * Note: the HTMLElement (of type TileLayoutHTMLElement) will store a `tileLayout: FeTileLayout` value that can be retrieve later
   * let tile layout = document.querySelector('.fe-tile-layout').feTileLayout;
   */
  public static init (options: FeTileLayoutOptions = {}, elOrString): FeTileLayout
  {
    // : FeTileLayoutElement = '.fe-tile-layout'
    if (typeof document === 'undefined')
      return null; // temp workaround SSR

    console.log("*************************************************");
    console.log("*************************************************");
    console.log(elOrString);
    let el = FeTileLayout.getLayoutElement(elOrString);
    if (!el)
    {
      if (typeof elOrString === 'string')
      {
        console.error('FeTileLayout.initAll() no tile layout was found with selector "' + elOrString + '" - element missing or wrong selector ?' +
          '\nNote: ".fe-tile-layout" is required for proper CSS styling and drag/drop, and is the default selector.');
      } else
      {
        console.error('FeTileLayout.init() no tile layout element was passed.');
      }
      return null;
    }

    if (!el.tileLayout)
    {
      el.tileLayout = new FeTileLayout(el, Utils.cloneDeep(options));
    }

    return el.tileLayout;
  }

  /**
   * Will initialize a list of elements (given a selector) and return an array of layouts.
   * @param options tile layout options (optional)
   * @param selector elements selector to convert to layouts (default to '.fe-tile-layout' class selector)
   *
   * @example
   * let layouts = FeTileLayout.initAll();
   * layouts.forEach(...)
   */
  public static initAll (options: FeTileLayoutOptions = {}, selector = '.fe-tile-layout'): FeTileLayout[]
  {
    let layouts: FeTileLayout[] = [];
    if (typeof document === 'undefined')
      return layouts; // temp workaround SSR

    FeTileLayout.getLayoutElements(selector).forEach(el =>
    {
      if (!el.tileLayout)
      {
        el.tileLayout = new FeTileLayout(el, Utils.cloneDeep(options));
      }
      layouts.push(el.tileLayout);
    });

    if (layouts.length === 0)
    {
      console.error('FeTileLayout.initAll() no tile layout was found with selector "' + selector + '" - element missing or wrong selector ?' +
        '\nNote: ".fe-tile-layout" is required for proper CSS styling and drag/drop, and is the default selector.');
    }

    return layouts;
  }

  /**
   * call to create a tile layout with the given options, including loading any children from JSON structure. This will call FeTileLayout.init(), then
   * layout.load() on any passed children (recursively). Great alternative to calling init() if you want entire tile layout to come from
   * JSON serialized data, including options.
   * @param parent HTML element parent to the tile layout
   * @param opt tile layout options used to initialize the tile layout, and list of children
   */
  public static addLayout (parent: HTMLElement, opt: FeTileLayoutOptions = {}): FeTileLayout
  {
    if (!parent)
      return null;

    let el = parent as FeTileLayoutHTMLElement;
    if (el.tileLayout)
    {
      // already a tile layout - set option and load data
      const tileLayout = el.tileLayout;
      if (opt)
        tileLayout.opts = { ...tileLayout.opts, ...opt };

      if (opt.children !== undefined)
        tileLayout.load(opt.children);

      return tileLayout;
    }

    // create the tile layout element, but check if the passed 'parent' already has tile layout styling and should be used instead
    const parentIsLayout = parent.classList.contains('fe-tile-layout');
    if (!parentIsLayout || FeTileLayout.addRemoveCB)
    {
      if (FeTileLayout.addRemoveCB)
      {
        el = FeTileLayout.addRemoveCB(parent, opt, true, true);
      } else
      {
        el = Utils.createDiv([ 'fe-tile-layout', opt.class ], parent);
      }
    }

    // create tile layout class and load any children
    let tileLayout = FeTileLayout.init(opt, el);
    return tileLayout;
  }

  /** call this method to register your engine instead of the default one.
   * See instead `FeTileLayoutOptions.engineClass` if you only need to
   * replace just one instance.
   */
  static registerEngine (engineClass: typeof FeTileLayoutEngine): void
  {
    FeTileLayout.engineClass = engineClass;
  }

  /**
   * callback method use when new items|layouts needs to be created or deleted, instead of the default
   * item: <div class="fe-tile-layout-item"><div class="fe-tile-layout-item-content">w.content</div></div>
   * layout: <div class="fe-tile-layout">layout content...</div>
   * add = true: the returned DOM element will then be converted to a FeTileLayoutItemHTMLElement using makeItem()|FeTileLayout:init().
   * add = false: the item will be removed from DOM (if not already done)
   * tile layout = true|false for tile layout vs layout-items
   */
  public static addRemoveCB?: AddRemoveFunction;

  /**
   * callback during saving to application can inject extra data for each item, on top of the tile layout layout properties
   */
  public static saveCB?: SaveFunction;

  /**
   * callback to create the content of items so the app can control how to store and restore it
   * By default this lib will do 'el.textContent = w.content' forcing text only support for avoiding potential XSS issues.
   */
  public static renderCB?: RenderFunction = (el: HTMLElement, w: Node) =>
  {
    // TODO content type handling??
    //if (el && w?.content)
    ///      el.innerHTML = w.content; // text only el.textContent = w.content; 
  };

  /** callback to use for resizeToContent instead of the built in one */
  public static resizeToContentCB?: ResizeToContentFunction;

  /** parent class for sizing content. defaults to '.fe-tile-layout-item-content' */
  public static resizeToContentParent = '.fe-tile-layout-item-content';

  /** scoping so users can call FeTileLayout.Utils.sort() for example */
  public static Utils = Utils;

  /** scoping so users can call new FeTileLayout.Engine(12) for example */
  public static Engine = FeTileLayoutEngine;

  /** engine used to implement non DOM tile layout functionality */
  public engine: FeTileLayoutEngine;

  /** point to a parent tile layout item if we're nested (inside a layout-item in between 2 Layouts) */
  public parentFeTileLayoutItem?: Node;

  protected static engineClass: typeof FeTileLayoutEngine;

  protected resizeObserver: ResizeObserver;

  /** @internal unique class name for our generated CSS style sheet */
  protected _styleSheetClass?: string;

  /** @internal true if we got created by drag over gesture, so we can removed on drag out (temporary) */
  public _isTemp?: boolean;

  /** @internal create placeholder DIV as needed */
  public get placeholder (): ItemHTMLElement
  {
    if (!this._placeholder)
    {
      let placeholderChild = document.createElement('div'); // child so padding match item-content
      placeholderChild.className = 'placeholder-content';
      if (this.opts.placeholderText)
      {
        placeholderChild.textContent = this.opts.placeholderText;
      }
      this._placeholder = document.createElement('div');
      this._placeholder.classList.add(this.opts.placeholderClass, feTileLayoutDefaults.itemClass, this.opts.itemClass);
      this.placeholder.appendChild(placeholderChild);
    }
    return this._placeholder;
  }

  /** @internal */
  protected _placeholder: HTMLElement;
  /** @internal prevent cached layouts from being updated when loading into small column layouts */
  protected _ignoreLayoutsNodeChange: boolean;
  /** @internal */
  public _gsEventHandler = {};
  /** @internal */
  protected _styles: TileLayoutCSSStyleSheet;
  /** @internal flag to keep cells square during resize */
  protected _isAutoCellHeight: boolean;
  /** @internal limit auto cell resizing method */
  protected _sizeThrottle: () => void;
  /** @internal limit auto cell resizing method */
  protected prevWidth: number;
  /** @internal extra row added when dragging at the bottom of the tile layout */
  protected _extraDragRow = 0;
  /** @internal true if nested tile layout should get column count from our width */
  protected _autoColumn?: boolean;
  /** @internal meant to store the scale of the active tile layout */
  protected dragTransform: DragTransform = { xScale: 1, yScale: 1, xOffset: 0, yOffset: 0 };
  private _skipInitialResize: boolean;

  /**
   * Construct a tile layout item from the given element and options
   * @param el the HTML element tied to this tile layout after it's been initialized
   * @param opts tile layout options - public for classes to access, but use methods to modify!
   */
  public constructor (public el: FeTileLayoutHTMLElement, public opts: FeTileLayoutOptions = {})
  {
    console.log(`tileLayout.ts Constructor -`, el, opts);

    el.tileLayout = this;
    opts = opts || {}; // handles null/undefined/0

    if (!el.classList.contains('fe-tile-layout'))
    {
      this.el.classList.add('fe-tile-layout');
    }

    // if row property exists, replace minRow and maxRow instead
    if (opts.row)
    {
      opts.minRow = opts.maxRow = opts.row;
      delete opts.row;
    }

    let rowAttr = Utils.toNumber(el.getAttribute('fe-tl-row'));

    // flag only valid in sub-layouts (handled by parent, not here)
    if (opts.column === 'auto')
    {
      delete opts.column;
    }

    // save original setting so we can restore on save
    if (opts.alwaysShowResizeHandle !== undefined)
    {
      (opts as InternalFeTileLayoutOptions)._alwaysShowResizeHandle = opts.alwaysShowResizeHandle;
    }

    let bk = opts.columnOpts?.breakpoints;
    // cleanup responsive opts (must have columnWidth | breakpoints) then sort breakpoints by size (so we can match during resize)
    const resp = opts.columnOpts;
    if (resp)
    {
      if (!resp.columnWidth && !resp.breakpoints?.length)
      {
        delete opts.columnOpts;
        bk = undefined;
      } else
      {
        resp.columnMax = resp.columnMax || 12;
      }
    }

    if (bk?.length > 1)
      bk.sort((a, b) => (b.w || 0) - (a.w || 0));

    // elements DOM attributes override any passed options (like CSS style) - merge the two together
    let defaults: FeTileLayoutOptions = {
      ...Utils.cloneDeep(feTileLayoutDefaults),
      column: Utils.toNumber(el.getAttribute('fe-tl-column')) || feTileLayoutDefaults.column,
      minRow: rowAttr ? rowAttr : Utils.toNumber(el.getAttribute('fe-tl-min-row')) || feTileLayoutDefaults.minRow,
      maxRow: rowAttr ? rowAttr : Utils.toNumber(el.getAttribute('fe-tl-max-row')) || feTileLayoutDefaults.maxRow,
      staticLayout: Utils.toBool(el.getAttribute('fe-tl-static')) || feTileLayoutDefaults.staticLayout,
      draggable: {
        handle: (opts.handleClass ? '.' + opts.handleClass : (opts.handle ? opts.handle : '')) || feTileLayoutDefaults.draggable.handle,
      },
      removableOptions: {
        accept: opts.itemClass || feTileLayoutDefaults.removableOptions.accept,
        decline: feTileLayoutDefaults.removableOptions.decline
      },
    };

    if (el.getAttribute('fe-tl-animate'))
    {
      // default to true, but if set to false use that instead
      defaults.animate = Utils.toBool(el.getAttribute('fe-tl-animate'));
    }

    opts = Utils.defaults(opts, defaults);
    this._initMargin(); // part of settings defaults...

    // Now check if we're loading into 1 column mode FIRST so we don't do un-necessary work (like cellHeight = width / 12 then go 1 column)
    this.checkDynamicColumn();
    this.el.classList.add('fe-tl-' + opts.column);

    if (opts.rtl === 'auto')
    {
      opts.rtl = (el.style.direction === 'rtl');
    }

    if (opts.rtl)
    {
      this.el.classList.add('fe-tile-layout-rtl');
    }

    // Check if we're nested within 1 to 4 layers, and if so update our style and keep pointer around (used during save)
    let currentElement: ItemHTMLElement | null = this.el;
    let parentFeTileLayoutItem: any = undefined;

    for (let i = 0; i < 4; i++)
    {
      currentElement = currentElement?.parentElement as ItemHTMLElement;
      if (!currentElement) 
        break;

      console.info(`tileLayout.ts currentElement at level ${ i + 1 } -`, currentElement);
      console.info(`tileLayout.ts currentElement.classList at level ${ i + 1 } -`, currentElement.classList);

      if (currentElement.classList.contains(feTileLayoutDefaults.itemClass))
      {
        parentFeTileLayoutItem = currentElement.tileLayoutNode;
        break;
      }
    }

    if (parentFeTileLayoutItem)
    {
      parentFeTileLayoutItem.subLayout = this;
      this.parentFeTileLayoutItem = parentFeTileLayoutItem;
      this.el.classList.add('fe-tile-layout-nested');
      parentFeTileLayoutItem.el.classList.add('fe-tile-layout-sub-layout');
    }

    this._isAutoCellHeight = (opts.cellHeight === 'auto');
    if (this._isAutoCellHeight || opts.cellHeight === 'initial')
    {
      // make the cell content square initially (will use resize/column event to keep it square)
      this.cellHeight(undefined, false);
    } else
    {
      // append unit if any are set
      if (typeof opts.cellHeight == 'number' && opts.cellHeightUnit && opts.cellHeightUnit !== feTileLayoutDefaults.cellHeightUnit)
      {
        opts.cellHeight = opts.cellHeight + opts.cellHeightUnit;
        delete opts.cellHeightUnit;
      }
      this.cellHeight(opts.cellHeight, false);
    }

    // see if we need to adjust auto-hide
    if (opts.alwaysShowResizeHandle === 'mobile')
    {
      opts.alwaysShowResizeHandle = isTouch;
    }

    this._styleSheetClass = 'fe-tl-id-' + FeTileLayoutEngine._idSeq++;
    this.el.classList.add(this._styleSheetClass);

    this._setStaticClass();

    let engineClass = opts.engineClass || FeTileLayout.engineClass || FeTileLayoutEngine;
    this.engine = new engineClass({
      column: this.getColumn(),
      float: opts.float,
      maxRow: opts.maxRow,
      onChange: (cbNodes) =>
      {
        let maxH = 0;
        this.engine.nodes.forEach(n => { maxH = Math.max(maxH, n.y + n.h); });
        cbNodes.forEach(n =>
        {
          let el = n.el;
          if (!el)
            return;

          if (n._removeDOM)
          {
            if (el)
              el.remove();

            delete n._removeDOM;
          } else
          {
            this._writePosAttr(el, n);
            console.log(`tileLayout.ts onChange -`, n);
          }
        });

        this._updateStyles(false, maxH); // false = don't recreate, just append if need be
      }
    });

    // create initial global styles BEFORE loading children so resizeToContent margin can be calculated correctly
    this._updateStyles(false, 0);

    if (opts.auto)
    {
      this.batchUpdate(); // prevent in between re-layout
      this.engine._loading = true; // loading collision check
      this.getFeTileLayoutItems().forEach(el => this._prepareElement(el));
      delete this.engine._loading;
      this.batchUpdate(false);
    }

    // load any passed in children as well, which overrides any DOM layout done above
    if (opts.children)
    {
      const children = opts.children;
      delete opts.children;
      if (children.length) 
        this.load(children); // don't load empty
    }

    this.setAnimation();

    // dynamic layouts require pausing during drag to detect over to nest vs push
    if (opts.subLayoutDynamic && !DragDropManager.pauseDrag) 
      DragDropManager.pauseDrag = true;

    if (opts.draggable?.pause !== undefined)
      DragDropManager.pauseDrag = opts.draggable.pause;

    this._setupRemoveDrop();
    this._setupAcceptItem();
    this._updateResizeEvent();
  }

  /**
   * add a new item and returns it.
   *
   * Item will be always placed even if result height is more than actual tile layout height.
   * You need to use `willItFit()` before calling addItem for additional check.
   * See also `makeItem(el)` for DOM element.
   *
   * @example
   * let tile layout = FeTileLayout.init();
   * layout.addItem({w: 3, content: 'hello'});
   *
   * @param w FeTileLayoutItem definition. used MakeItem(el) if you have dom element instead.
   * @param options item position/size options (optional, and ignore if first param is already option) - see FeTileLayoutItem
   */
  public addItem (w: FeTileLayoutItem): ItemHTMLElement
  {
    let el: ItemHTMLElement;
    let node: Node = w;
    if (node?.el)
    {
      el = node.el; // re-use element stored in the node
    } else if (FeTileLayout.addRemoveCB)
    {
      el = FeTileLayout.addRemoveCB(this.el, w, true, false);
    } else
    {
      el = Utils.createItemDivs(this.opts.itemClass, w);
    }

    if (!el) return;

    // if the caller ended up initializing the item in addRemoveCB, or we stared with one already, skip the rest
    node = el.tileLayoutNode;
    if (node && el.parentElement === this.el && this.engine.nodes.find(n => n._id === node._id))
      return el;

    // Tempting to initialize the passed in opt with default and valid values, but this break knockout demos
    // as the actual value are filled in when _prepareElement() calls el.getAttribute('fe-tl-xyz') before adding the node.
    // So make sure we load any DOM attributes that are not specified in passed in options (which override)
    let domAttr = this._readAttr(el);
    Utils.defaults(w, domAttr);
    this.engine.prepareNode(w);
    this._writeAttr(el, w);

    this.el.appendChild(el);

    this.makeItem(el, w);

    return el;
  }

  /**
   * Convert an existing layoutItem element into a sub-layout with the given (optional) options, else inherit them
   * from the parent's subLayout options.
   * @param el layoutItem element to convert
   * @param ops (optional) sub-layout options, else default to node, then parent settings, else defaults
   * @param nodeToAdd (optional) node to add to the newly created sub tile layout (used when dragging over existing regular item)
   * @param saveContent if true (default) the html inside .fe-tile-layout-content will be saved to child item
   * @returns newly created layout
   */
  public makeSubLayout (el: ItemHTMLElement, ops?: FeTileLayoutOptions, nodeToAdd?: Node, saveContent = true): FeTileLayout
  {
    let node = el.tileLayoutNode;
    if (!node)
    {
      node = this.makeItem(el).tileLayoutNode;
    }
    if (node.subLayout?.el) 
      return node.subLayout; // already done

    // find the template subLayout stored on a parent as fallback...
    let subLayoutTemplate: FeTileLayoutOptions; // eslint-disable-next-line @typescript-eslint/no-this-alias
    let tileLayout: FeTileLayout = this;
    while (tileLayout && !subLayoutTemplate)
    {
      subLayoutTemplate = tileLayout.opts?.subLayoutOpts;
      tileLayout = tileLayout.parentFeTileLayoutItem?.tileLayout;
    }
    //... and set the create options
    ops = Utils.cloneDeep({ ...(subLayoutTemplate || {}), children: undefined, ...(ops || node.subLayoutOpts || {}) });
    node.subLayoutOpts = ops;

    // if column special case it set, remember that flag and set default
    let autoColumn: boolean;
    if (ops.column === 'auto')
    {
      autoColumn = true;
      ops.column = Math.max(node.w || 1, nodeToAdd?.w || 1);
      delete ops.columnOpts; // driven by parent
    }

    // if we're converting an existing full item, move over the content to be the first sub item in the new layout
    let content = node.el.querySelector('.fe-tile-layout-item-content') as HTMLElement;
    let newItem: HTMLElement;
    let newItemOpt: Node;
    if (saveContent)
    {
      this._removeDD(node.el); // remove D&D since it's set on content div
      newItemOpt = { ...node, x: 0, y: 0 };
      Utils.removeInternalForSave(newItemOpt);
      delete newItemOpt.subLayoutOpts;
      //if (node.content)
      //{
      //  newItemOpt.content = node.content;
      //  delete node.content;
      //}

      if (FeTileLayout.addRemoveCB)
      {
        newItem = FeTileLayout.addRemoveCB(this.el, newItemOpt, true, false);
      } else
      {
        newItem = Utils.createDiv([ 'fe-tile-layout-item' ]);
        newItem.appendChild(content);
        content = Utils.createDiv([ 'fe-tile-layout-item-content' ], node.el);
      }
      this._prepareDragDropByNode(node); // ... and restore original D&D
    }

    // if we're adding an additional item, make the container large enough to have them both
    if (nodeToAdd)
    {
      let w = autoColumn ? ops.column : node.w;
      let h = node.h + nodeToAdd.h;
      let style = node.el.style;
      style.transition = 'none'; // show up instantly so we don't see scrollbar with nodeToAdd
      this.update(node.el, { w, h });
      setTimeout(() => style.transition = null); // recover animation
    }

    let subLayout = node.subLayout = FeTileLayout.addLayout(content, ops);
    if (nodeToAdd?._moving) 
      subLayout._isTemp = true; // prevent re-nesting as we add over

    if (autoColumn) 
      subLayout._autoColumn = true;

    // add the original content back as a child of hte newly created layout
    if (saveContent)
    {
      subLayout.makeItem(newItem, newItemOpt);
    }

    // now add any additional node
    if (nodeToAdd)
    {
      if (nodeToAdd._moving)
      {
        // create an artificial event even for the just created tile layout to receive this item
        window.setTimeout(() => Utils.simulateMouseEvent(nodeToAdd._event, 'mouseenter', subLayout.el), 0);
      } else
      {
        subLayout.makeItem(node.el, node);
      }
    }
    return subLayout;
  }

  /**
   * called when an item was converted into a nested tile layout to accommodate a dragged over item, but then item leaves - return back
   * to the original layout-item. Also called to remove empty sub-layouts when last item is dragged out (since re-creating is simple)
   */
  public removeAssubLayout (nodeThatRemoved?: Node): void
  {
    let pLayout = this.parentFeTileLayoutItem?.tileLayout;
    if (!pLayout)
      return;

    pLayout.batchUpdate();
    pLayout.removeItem(this.parentFeTileLayoutItem.el, true, true);
    this.engine.nodes.forEach(n =>
    {
      // migrate any children over and offsetting by our location
      n.x += this.parentFeTileLayoutItem.x;
      n.y += this.parentFeTileLayoutItem.y;
      pLayout.makeItem(n.el, n);
    });
    
    pLayout.batchUpdate(false);
    if (this.parentFeTileLayoutItem) 
      delete this.parentFeTileLayoutItem.subLayout;

    delete this.parentFeTileLayoutItem;

    // create an artificial event for the original tile layout now that this one is gone (got a leave, but won't get enter)
    if (nodeThatRemoved)
    {
      window.setTimeout(() => Utils.simulateMouseEvent(nodeThatRemoved._event, 'mouseenter', pLayout.el), 0);
    }
  }

  /**
   * saves the current layout returning a list of items for serialization which might include any nested layouts.
   * @param saveContent if true (default) the latest html inside .fe-tile-layout-content will be saved to FeTileLayoutItem.content field, else it will
   * be removed.
   * @param saveLayoutOpt if true (default false), save the tile layout options itself, so you can call the new FeTileLayout.addLayout()
   * to recreate everything from scratch. FeTileLayoutOptions.children would then contain the item list instead.
   * @param saveCB callback for each node -> item, so application can insert additional data to be saved into the item data structure.
   * @returns list of items or full tile layout option, including .children list of items
   */
  public save (saveContent = true, saveLayoutOpt = false, saveCB = FeTileLayout.saveCB): FeTileLayoutItem[] | FeTileLayoutOptions
  {
    // return copied FeTileLayoutItem (with optionally .el) we can modify at will...
    let list = this.engine.save(saveContent, saveCB);

    // check for HTML content and nested layouts
    list.forEach(n =>
    {
      if (saveContent && n.el && !n.subLayout && !saveCB)
      { // sub-layout are saved differently, not plain content
        const itemContent = n.el.querySelector('.fe-tile-layout-item-content');
        //n.content = itemContent?.innerHTML;
        //if (!n.content) 
        //  delete n.content;
      } else
      {
        //if (!saveContent && !saveCB) { 
        //  delete n.content; 
        //}
        // check for nested layout
        if (n.subLayout?.el)
        {
          const listOrOpt = n.subLayout.save(saveContent, saveLayoutOpt, saveCB);
          n.subLayoutOpts = (saveLayoutOpt ? listOrOpt : { children: listOrOpt }) as FeTileLayoutOptions;
          delete n.subLayout;
        }
      }
      delete n.el;
    });

    // check if save entire tile layout options (needed for recursive) + children...
    if (saveLayoutOpt)
    {
      let o: InternalFeTileLayoutOptions = Utils.cloneDeep(this.opts);
      // delete default values that will be recreated on launch
      if (o.marginBottom === o.marginTop && o.marginRight === o.marginLeft && o.marginTop === o.marginRight)
      {
        o.margin = o.marginTop;
        delete o.marginTop;
        delete o.marginRight; 
        delete o.marginBottom; 
        delete o.marginLeft;
      }

      if (o.rtl === (this.el.style.direction === 'rtl'))
      {
        o.rtl = 'auto';
      }

      if (this._isAutoCellHeight)
      {
        o.cellHeight = 'auto';
      }

      if (this._autoColumn)
      {
        o.column = 'auto';
      }

      const origShow = o._alwaysShowResizeHandle;
      delete o._alwaysShowResizeHandle;
      if (origShow !== undefined)
      {
        o.alwaysShowResizeHandle = origShow;
      } else
      {
        delete o.alwaysShowResizeHandle;
      }
      Utils.removeInternalAndSame(o, feTileLayoutDefaults);
      o.children = list;
      return o;
    }

    return list;
  }

  /**
   * load the items from a list. This will call update() on each (matching by id) or add/remove items that are not there.
   *
   * @param layout list of items definition to update/create
   * @param addAndRemove boolean (default true) or callback method can be passed to control if and how missing items can be added/removed, giving
   * the user control of insertion.
   */
  public load (items: FeTileLayoutItem[], addRemove: boolean | AddRemoveFunction = FeTileLayout.addRemoveCB || true): FeTileLayout
  {
    console.log(`tileLayout.ts Load Entry -`, items, addRemove);

    if (addRemove === undefined || addRemove === null)
      addRemove = true;

    items = Utils.cloneDeep(items); // so we can mod
    const column = this.getColumn();

    // make sure size 1x1 (default) is present as it may need to override current sizes
    items.forEach(n => { n.w = n.w || 1; n.h = n.h || 1; });

    // sort items. those without coord will be appended last
    items = Utils.sort(items);

    // if we're loading a layout into for example 1 column and items don't fit, make sure to save
    // the original wanted layout so we can scale back up correctly #1471
    let maxColumn = 0;
    items.forEach(n => { maxColumn = Math.max(maxColumn, (n.x || 0) + n.w); });
    if (maxColumn > column)
    {
      this._ignoreLayoutsNodeChange = true; // skip layout update
      this.engine.cacheLayout(items, maxColumn, true);
    }

    // if given a different callback, temporally set it as global option so creating will use it
    const prevCB = FeTileLayout.addRemoveCB;
    if (typeof (addRemove) === 'function')
      FeTileLayout.addRemoveCB = addRemove as AddRemoveFunction;

    let removed: Node[] = [];
    this.batchUpdate();

    // if we are loading from empty temporarily remove animation
    const blank = !this.engine.nodes.length;
    if (blank) this.setAnimation(false);

    // see if any items are missing from new layout and need to be removed first
    if (!blank && addRemove)
    {
      let copyNodes = [ ...this.engine.nodes ]; // don't loop through array you modify
      copyNodes.forEach(n =>
      {
        if (!n.id)
          return;

        let item = Utils.find(items, n.id);
        if (!item)
        {
          if (FeTileLayout.addRemoveCB)
            FeTileLayout.addRemoveCB(this.el, n, false, false);

          removed.push(n); // batch keep track
          this.removeItem(n.el, true, false);
        }
      });
    }

    // now add/update the items - starting with removing items in the new layout we will reposition
    // to reduce collision and add no-coord ones at next available spot
    this.engine._loading = true; // help with collision
    let updateNodes: FeTileLayoutItem[] = [];
    this.engine.nodes = this.engine.nodes.filter(n =>
    {
      if (Utils.find(items, n.id))
      {
        updateNodes.push(n); 
        return false;
      } // remove if found from list
      return true;
    });

    console.log(`tileLayout.ts Load AAAA -`, items);

    items.forEach(w =>
    {
      let item = Utils.find(updateNodes, w.id);
      if (item)
      {
        // if item sizes to content, re-use the exiting height so it's a better guess at the final size (same if width doesn't change)
        if (Utils.shouldSizeToContent(item))
          w.h = item.h;

        // check if missing coord, in which case find next empty slot with new (or old if missing) sizes
        this.engine.nodeBoundFix(w);
        if (w.autoPosition || w.x === undefined || w.y === undefined)
        {
          w.w = w.w || item.w;
          w.h = w.h || item.h;
          this.engine.findEmptyPosition(w);
        }

        // add back to current list BUT force a collision check if it 'appears' we didn't change to make sure we don't overlap others now
        this.engine.nodes.push(item);
        console.log(`tileLayout.ts Load item -`, item);
        if (Utils.samePos(item, w))
        {
          this.moveNode(item, { ...w, forceCollide: true });
        }

        this.update(item.el, w);
        if (w.subLayoutOpts?.children)
        {
          // update any sub tile layout as well
          let sub = item.el.querySelector('.fe-tile-layout') as FeTileLayoutHTMLElement;
          if (sub && sub.tileLayout)
          {
            sub.tileLayout.load(w.subLayoutOpts.children); // TODO: support updating tile layout options ?
          }
        }
      } else if (addRemove)
      {
        console.log(`tileLayout.ts Load item addItem -`, item, w);
        this.addItem(w);
      }
      else
      {
        console.log(`tileLayout.ts Load item addRemove was not truthy -`, item, w, addRemove);
      }
    });

    delete this.engine._loading; // done loading
    this.engine.removedNodes = removed;
    this.batchUpdate(false);

    // after commit, clear that flag
    delete this._ignoreLayoutsNodeChange;
    prevCB ? FeTileLayout.addRemoveCB = prevCB : delete FeTileLayout.addRemoveCB;
    // delay adding animation back
    if (blank && this.opts?.animate)
      this.setAnimation(this.opts.animate, true);

    console.log(`tileLayout.ts Load Exit -`, this.getFeTileLayoutItems());

    return this;
  }

  /**
   * use before calling a bunch of `addItem()` to prevent un-necessary relayouts in between (more efficient)
   * and get a single event callback. You will see no changes until `batchUpdate(false)` is called.
   */
  public batchUpdate (flag = true): FeTileLayout
  {
    this.engine.batchUpdate(flag);
    if (!flag)
    {
      this._updateContainerHeight();
      this._triggerRemoveEvent();
      this._triggerAddEvent();
      this._triggerChangeEvent();
    }
    return this;
  }

  /**
   * Gets current cell height.
   */
  public getCellHeight (forcePixel = false): number
  {
    if (this.opts.cellHeight && this.opts.cellHeight !== 'auto' &&
      (!forcePixel || !this.opts.cellHeightUnit || this.opts.cellHeightUnit === 'px'))
    {
      return this.opts.cellHeight as number;
    }

    // do rem/em/cm/mm to px conversion
    if (this.opts.cellHeightUnit === 'rem')
    {
      return (this.opts.cellHeight as number) * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    if (this.opts.cellHeightUnit === 'em')
    {
      return (this.opts.cellHeight as number) * parseFloat(getComputedStyle(this.el).fontSize);
    }

    if (this.opts.cellHeightUnit === 'cm')
    {
      // 1cm = 96px/2.54. See https://www.w3.org/TR/css-values-3/#absolute-lengths
      return (this.opts.cellHeight as number) * (96 / 2.54);
    }

    if (this.opts.cellHeightUnit === 'mm')
    {
      return (this.opts.cellHeight as number) * (96 / 2.54) / 10;
    }

    // else get first cell height
    let el = this.el.querySelector('.' + this.opts.itemClass) as HTMLElement;
    if (el)
    {
      let h = Utils.toNumber(el.getAttribute('fe-tl-h')) || 1; // since we don't write 1 anymore
      return Math.round(el.offsetHeight / h);
    }

    // else do entire tile layout and # of rows (but doesn't work if min-height is the actual constrain)
    let rows = parseInt(this.el.getAttribute('fe-tl-current-row'));
    return rows ? Math.round(this.el.getBoundingClientRect().height / rows) : this.opts.cellHeight as number;
  }

  /**
   * Update current cell height - see `FeTileLayoutOptions.cellHeight` for format.
   * This method rebuilds an internal CSS style sheet.
   * Note: You can expect performance issues if you call this method too often.
   *
   * @param val the cell height. If not passed (undefined), cells content will be made square (match width minus margin),
   * if pass 0 the CSS will be generated by the application instead.
   * @param update (Optional) if false, styles will not be updated
   *
   * @example
   * layout.cellHeight(100); // same as 100px
   * layout.cellHeight('70px');
   * layout.cellHeight(layout.cellWidth() * 1.2);
   */
  public cellHeight (val?: NumberOrString, update = true): FeTileLayout
  {

    // if not called internally, check if we're changing mode
    if (update && val !== undefined)
    {
      if (this._isAutoCellHeight !== (val === 'auto'))
      {
        this._isAutoCellHeight = (val === 'auto');
        this._updateResizeEvent();
      }
    }
    if (val === 'initial' || val === 'auto')
    {
      val = undefined;
    }

    // make item content be square
    if (val === undefined)
    {
      let marginDiff = - (this.opts.marginRight as number) - (this.opts.marginLeft as number)
        + (this.opts.marginTop as number) + (this.opts.marginBottom as number);
      val = this.cellWidth() + marginDiff;
    }

    let data = Utils.parseHeight(val);
    if (this.opts.cellHeightUnit === data.unit && this.opts.cellHeight === data.h)
    {
      return this;
    }
    this.opts.cellHeightUnit = data.unit;
    this.opts.cellHeight = data.h;

    this.resizeToContentCheck();

    if (update)
    {
      this._updateStyles(true); // true = force re-create for current # of rows
    }

    return this;
  }

  /** Gets current cell width. */
  public cellWidth (): number
  {
    return this._widthOrContainer() / this.getColumn();
  }

  /** return our expected width (or parent) , and optionally of window for dynamic column check */
  protected _widthOrContainer (forBreakpoint = false): number
  {
    // use `offsetWidth` or `clientWidth` (no scrollbar) ?
    // https://stackoverflow.com/questions/21064101/understanding-offsetwidth-clientwidth-scrollwidth-and-height-respectively
    return forBreakpoint && this.opts.columnOpts?.breakpointForWindow ? window.innerWidth : (this.el.clientWidth || this.el.parentElement.clientWidth || window.innerWidth);
  }

  /** checks for dynamic column count for our current size, returning true if changed */
  protected checkDynamicColumn (): boolean
  {
    const resp = this.opts.columnOpts;
    if (!resp || (!resp.columnWidth && !resp.breakpoints?.length))
      return false;

    const column = this.getColumn();
    let newColumn = column;
    const w = this._widthOrContainer(true);
    if (resp.columnWidth)
    {
      newColumn = Math.min(Math.round(w / resp.columnWidth) || 1, resp.columnMax);
    } else
    {
      // find the closest breakpoint (already sorted big to small) that matches
      newColumn = resp.columnMax;
      let i = 0;
      while (i < resp.breakpoints.length && w <= resp.breakpoints[ i ].w)
      {
        newColumn = resp.breakpoints[ i++ ].c || column;
      }
    }
    if (newColumn !== column)
    {
      const bk = resp.breakpoints?.find(b => b.c === newColumn);
      this.column(newColumn, bk?.layout || resp.layout);
      return true;
    }
    return false;
  }

  /**
   * re-layout tile layout items to reclaim any empty space. Options are:
   * 'list' keep the item left->right order the same, even if that means leaving an empty slot if things don't fit
   * 'compact' might re-order items to fill any empty space
   *
   * doSort - 'false' to let you do your own sorting ahead in case you need to control a different order. (default to sort)
   */
  public compact (layout: CompactOptions = 'compact', doSort = true): FeTileLayout
  {
    this.engine.compact(layout, doSort);
    this._triggerChangeEvent();
    return this;
  }

  /**
   * set the number of columns in the layout. Will update existing items to conform to new number of columns,
   * as well as cache the original layout so you can revert back to previous positions without loss.
   * @param column - Integer > 0 (default 12).
   * @param layout specify the type of re-layout that will happen (position, size, etc...).
   * Note: items will never be outside of the current column boundaries. default ('moveScale'). Ignored for 1 column
   */
  public column (column: number, layout: ColumnOptions = 'moveScale'): FeTileLayout
  {
    if (!column || column < 1 || this.opts.column === column) 
      return this;

    let oldColumn = this.getColumn();
    this.opts.column = column;
    if (!this.engine) 
      return this; // called in constructor, noting else to do

    this.engine.column = column;
    this.el.classList.remove('fe-tl-' + oldColumn);
    this.el.classList.add('fe-tl-' + column);

    // update the items now, checking if we have a custom children layout
    this.engine.columnChanged(oldColumn, column, layout);
    if (this._isAutoCellHeight) 
      this.cellHeight();

    this.resizeToContentCheck(true); // wait for width resizing

    // and trigger our event last...
    this._ignoreLayoutsNodeChange = true; // skip layout update
    this._triggerChangeEvent();
    delete this._ignoreLayoutsNodeChange;

    return this;
  }

  /**
   * get the number of columns in the tile layout (default 12)
   */
  public getColumn (): number { return this.opts.column as number; }

  /** returns an array of tile layout HTML elements (no placeholder) - used to iterate through our children in DOM order */
  public getFeTileLayoutItems (): ItemHTMLElement[]
  {
    return Array.from(this.el.children)
      .filter((el: HTMLElement) => el.matches('.' + this.opts.itemClass) && !el.matches('.' + this.opts.placeholderClass)) as ItemHTMLElement[];
  }

  /**
   * Destroys a tile layout instance. DO NOT CALL any methods or access any vars after this as it will free up members.
   * @param removeDOM if `false` tile layout and items HTML elements will not be removed from the DOM (Optional. Default `true`).
   */
  public destroy (removeDOM = true): FeTileLayout
  {
    if (!this.el)
      return; // prevent multiple calls

    this.offAll();
    this._updateResizeEvent(true);
    this.setStatic(true, false); // permanently removes DD but don't set CSS class (we're going away)
    this.setAnimation(false);
    if (!removeDOM)
    {
      this.removeAll(removeDOM);
      this.el.classList.remove(this._styleSheetClass);
      this.el.removeAttribute('fe-tl-current-row');
    } else
    {
      this.el.parentNode.removeChild(this.el);
    }

    this._removeStylesheet();
    if (this.parentFeTileLayoutItem)
      delete this.parentFeTileLayoutItem.subLayout;

    delete this.parentFeTileLayoutItem;
    delete this.opts;
    delete this._placeholder;
    delete this.engine;
    delete this.el.tileLayout; // remove circular dependency that would prevent a freeing
    delete this.el;
    return this;
  }

  /**
   * enable/disable floating items (default: `false`)
   */
  public float (val: boolean): FeTileLayout
  {
    if (this.opts.float !== val)
    {
      this.opts.float = this.engine.float = val;
      this._triggerChangeEvent();
    }
    return this;
  }

  /**
   * get the current float mode
   */
  public getFloat (): boolean
  {
    return this.engine.float;
  }

  /**
   * Get the position of the cell under a pixel on screen.
   * @param position the position of the pixel to resolve in
   * absolute coordinates, as an object with top and left properties
   * @param useDocRelative if true, value will be based on document position vs parent position (Optional. Default false).
   * Useful when tile layout is within `position: relative` element
   *
   * Returns an object with properties `x` and `y` i.e. the column and row in the layout.
   */
  public getCellFromPixel (position: MousePosition, useDocRelative = false): CellPosition
  {
    let box = this.el.getBoundingClientRect();
    // console.log(`getBoundingClientRect left: ${box.left} top: ${box.top} w: ${box.w} h: ${box.h}`)
    let containerPos: { top: number, left: number; };
    if (useDocRelative)
    {
      containerPos = { top: box.top + document.documentElement.scrollTop, left: box.left };
      // console.log(`getCellFromPixel scrollTop: ${document.documentElement.scrollTop}`)
    } else
    {
      containerPos = { top: this.el.offsetTop, left: this.el.offsetLeft };
      // console.log(`getCellFromPixel offsetTop: ${containerPos.left} offsetLeft: ${containerPos.top}`)
    }
    let relativeLeft = position.left - containerPos.left;
    let relativeTop = position.top - containerPos.top;

    let columnWidth = (box.width / this.getColumn());
    let rowHeight = (box.height / parseInt(this.el.getAttribute('fe-tl-current-row')));

    return { x: Math.floor(relativeLeft / columnWidth), y: Math.floor(relativeTop / rowHeight) };
  }

  /** returns the current number of rows, which will be at least `minRow` if set */
  public getRow (): number
  {
    return Math.max(this.engine.getRow(), this.opts.minRow);
  }

  /**
   * Checks if specified area is empty.
   * @param x the position x.
   * @param y the position y.
   * @param w the width of to check
   * @param h the height of to check
   */
  public isAreaEmpty (x: number, y: number, w: number, h: number): boolean
  {
    return this.engine.isAreaEmpty(x, y, w, h);
  }

  /**
   * If you add elements to your tile layout by hand (or have some framework creating DOM), you have to tell layout afterwards to make them items.
   * If you want layout to add the elements for you, use `addItem()` instead.
   * Makes the given element a item and returns it.
   * @param els item or single selector to convert.
   * @param options item definition to use instead of reading attributes or using default sizing values
   *
   * @example
   * let tile layout = FeTileLayout.init();
   * layout.el.appendChild('<div id="1" fe-tl-w="3"></div>');
   * layout.el.appendChild('<div id="2"></div>');
   * layout.makeItem('1');
   * layout.makeItem('2', {w:2, content: 'hello'});
   */
  public makeItem (els: FeTileLayoutElement, options?: FeTileLayoutItem): ItemHTMLElement
  {
    let el = FeTileLayout.getElement(els);
    if (!el)
      return;

    if (!el.parentElement)
      this.el.appendChild(el);

    this._prepareElement(el, true, options);
    const node = el.tileLayoutNode;

    this._updateContainerHeight();

    // see if there is a sub-layout to create
    if (node.subLayoutOpts)
    {
      this.makeSubLayout(el, node.subLayoutOpts, undefined, false); // node.subLayout will be used as option in method, no need to pass
    }

    // if we're adding an item into 1 column make sure
    // we don't override the larger 12 column layout that was already saved.
    if (this.opts.column === 1)
    {
      this._ignoreLayoutsNodeChange = true;
    }
    this._triggerAddEvent();
    this._triggerChangeEvent();
    delete this._ignoreLayoutsNodeChange;

    return el;
  }

  /**
   * Event handler that extracts our CustomEvent data out automatically for receiving custom
   * notifications (see doc for supported events)
   * @param name of the event (see possible values) or list of names space separated
   * @param callback function called with event and optional second/third param
   * (see README documentation for each signature).
   *
   * @example
   * layout.on('added', function(e, items) { log('added ', items)} );
   * or
   * layout.on('added removed change', function(e, items) { log(e.type, items)} );
   *
   * Note: in some cases it is the same as calling native handler and parsing the event.
   * layout.el.addEventListener('added', function(event) { log('added ', event.detail)} );
   *
   */
  public on (name: 'dropped', callback: DroppedHandler): FeTileLayout;
  public on (name: 'enable' | 'disable', callback: EventHandler): FeTileLayout;
  public on (name: 'change' | 'added' | 'removed' | 'resizecontent', callback: NodesHandler): FeTileLayout;
  public on (name: 'resizestart' | 'resize' | 'resizestop' | 'dragstart' | 'drag' | 'dragstop', callback: ElementHandler): FeTileLayout;
  public on (name: string, callback: EventHandlerCallback): FeTileLayout;
  public on (name: FeTileLayoutEvent | string, callback: EventHandlerCallback): FeTileLayout
  {
    // check for array of names being passed instead
    if (name.indexOf(' ') !== -1)
    {
      let names = name.split(' ') as FeTileLayoutEvent[];
      names.forEach(name => this.on(name, callback));
      return this;
    }

    // native CustomEvent handlers - cash the generic handlers so we can easily remove
    if (name === 'change' || name === 'added' || name === 'removed' || name === 'enable' || name === 'disable')
    {
      let noData = (name === 'enable' || name === 'disable');
      if (noData)
      {
        this._gsEventHandler[ name ] = (event: Event) => (callback as EventHandler)(event);
      } else
      {
        this._gsEventHandler[ name ] = (event: CustomEvent) => { if (event.detail) (callback as NodesHandler)(event, event.detail); };
      }
      this.el.addEventListener(name, this._gsEventHandler[ name ]);
    } else if (name === 'drag' || name === 'dragstart' || name === 'dragstop' || name === 'resizestart' || name === 'resize'
      || name === 'resizestop' || name === 'dropped' || name === 'resizecontent')
    {
      console.log(`tileLayout.ts on callback - ${ name } - ${ callback }`);
      // drag&drop stop events NEED to be call them AFTER we update node attributes so handle them ourself.
      // do same for start event to make it easier...
      this._gsEventHandler[ name ] = callback;
    } else
    {
      console.error('FeTileLayout.on(' + name + ') event not supported');
    }
    return this;
  }

  /**
   * unsubscribe from the 'on' event FeTileLayoutEvent
   * @param name of the event (see possible values) or list of names space separated
   */
  public off (name: FeTileLayoutEvent | string): FeTileLayout
  {
    // check for array of names being passed instead
    if (name.indexOf(' ') !== -1)
    {
      let names = name.split(' ') as FeTileLayoutEvent[];
      names.forEach(name => this.off(name));
      return this;
    }

    if (name === 'change' || name === 'added' || name === 'removed' || name === 'enable' || name === 'disable')
    {
      // remove native CustomEvent handlers
      if (this._gsEventHandler[ name ])
      {
        this.el.removeEventListener(name, this._gsEventHandler[ name ]);
      }
    }
    delete this._gsEventHandler[ name ];

    return this;
  }

  /** remove all event handlers */
  public offAll (): FeTileLayout
  {
    Object.keys(this._gsEventHandler).forEach((key: FeTileLayoutEvent) => this.off(key));
    return this;
  }

  /**
   * Removes item from the layout.
   * @param el  item or selector to modify
   * @param removeDOM if `false` DOM element won't be removed from the tree (Default? true).
   * @param triggerEvent if `false` (quiet mode) element will not be added to removed list and no 'removed' callbacks will be called (Default? true).
   */
  public removeItem (els: FeTileLayoutElement, removeDOM = true, triggerEvent = true): FeTileLayout
  {
    if (!els)
    {
      console.error('Error: FeTileLayout.removeItem(undefined) called');
      return this;
    }

    FeTileLayout.getElements(els).forEach(el =>
    {
      if (el.parentElement && el.parentElement !== this.el)
        return; // not our child!

      let node = el.tileLayoutNode;
      // For Meteor support
      if (!node)
      {
        node = this.engine.nodes.find(n => el === n.el);
      }

      if (!node)
        return;

      if (removeDOM && FeTileLayout.addRemoveCB)
      {
        FeTileLayout.addRemoveCB(this.el, node, false, false);
      }

      // remove our DOM data (circular link) and drag&drop permanently
      delete el.tileLayoutNode;
      this._removeDD(el);

      this.engine.removeNode(node, removeDOM, triggerEvent);

      if (removeDOM && el.parentElement)
      {
        el.remove(); // in batch mode engine.removeNode doesn't call back to remove DOM
      }
    });

    if (triggerEvent)
    {
      this._triggerRemoveEvent();
      this._triggerChangeEvent();
    }
    return this;
  }

  /**
   * Removes all items from the layout.
   * @param removeDOM if `false` DOM elements won't be removed from the tree (Default? `true`).
   * @param triggerEvent if `false` (quiet mode) element will not be added to removed list and no 'removed' callbacks will be called (Default? true).
   */
  public removeAll (removeDOM = true, triggerEvent = true): FeTileLayout
  {
    // always remove our DOM data (circular link) before list gets emptied and drag&drop permanently
    this.engine.nodes.forEach(n =>
    {
      if (removeDOM && FeTileLayout.addRemoveCB)
      {
        FeTileLayout.addRemoveCB(this.el, n, false, false);
      }
      delete n.el.tileLayoutNode;
      if (!this.opts.staticLayout)
        this._removeDD(n.el);
    });

    this.engine.removeAll(removeDOM, triggerEvent);
    if (triggerEvent)
      this._triggerRemoveEvent();

    return this;
  }

  /**
   * Toggle the tile layout animation state.  Toggles the `fe-tile-layout-animate` class.
   * @param doAnimate if true the tile layout will animate.
   * @param delay if true setting will be set on next event loop.
   */
  public setAnimation (doAnimate = this.opts.animate, delay?: boolean): FeTileLayout
  {
    if (delay)
    {
      // delay, but check to make sure tile layout (opt) is still around
      setTimeout(() =>
      {
        if (this.opts) this.setAnimation(doAnimate);
      });
    } else if (doAnimate)
    {
      this.el.classList.add('fe-tile-layout-animate');
    } else
    {
      this.el.classList.remove('fe-tile-layout-animate');
    }
    return this;
  }

  /** @internal */
  private hasAnimationCSS (): boolean
  {
    return this.el.classList.contains('fe-tile-layout-animate');
  }

  /**
   * Toggle the tile layout static state, which permanently removes/add Drag&Drop support, unlike disable()/enable() that just turns it off/on.
   * Also toggle the fe-tile-layout-static class.
   * @param val if true the tile layout become static.
   * @param updateClass true (default) if css class gets updated
   * @param recurse true (default) if sub-layouts also get updated
   */
  public setStatic (val: boolean, updateClass = true, recurse = true): FeTileLayout
  {
    if (!!this.opts.staticLayout === val)
      return this;

    val ? this.opts.staticLayout = true : delete this.opts.staticLayout;
    this._setupRemoveDrop();
    this._setupAcceptItem();
    this.engine.nodes.forEach(n =>
    {
      this._prepareDragDropByNode(n); // either delete or init Drag&drop
      if (n.subLayout && recurse) 
        n.subLayout.setStatic(val, updateClass, recurse);
    });

    if (updateClass)
    {
      this._setStaticClass();
    }

    return this;
  }

  /**
   * Updates item position/size and other info. Note: if you need to call this on all nodes, use load() instead which will update what changed.
   * @param els  item or selector of objects to modify (note: setting the same x,y for multiple items will be indeterministic and likely unwanted)
   * @param opt new item options (x,y,w,h, etc..). Only those set will be updated.
   */
  public update (els: FeTileLayoutElement, opt: FeTileLayoutItem): FeTileLayout
  {
    FeTileLayout.getElements(els).forEach(el =>
    {
      let n = el?.tileLayoutNode;
      if (!n)
        return;

      let w = Utils.cloneDeep(opt); // make a copy we can modify in case they re-use it or multiple items
      this.engine.nodeBoundFix(w);
      delete w.autoPosition;
      delete w.id;

      // move/resize item if anything changed
      let keys = [ 'x', 'y', 'w', 'h' ];
      let m: FeTileLayoutItem;
      if (keys.some(k => w[ k ] !== undefined && w[ k ] !== n[ k ]))
      {
        m = {};
        keys.forEach(k =>
        {
          m[ k ] = (w[ k ] !== undefined) ? w[ k ] : n[ k ];
          delete w[ k ];
        });
      }
      // for a move as well IF there are any min/max fields set
      if (!m && (w.minW || w.minH || w.maxW || w.maxH))
      {
        m = {}; // will use node position but validate values
      }

      // check for content changing
      //if (w.content !== undefined)
      //{
      //  const itemContent = el.querySelector('.fe-tile-layout-item-content') as HTMLElement;
      //  if (itemContent && itemContent.textContent !== w.content)
      //  {
      //    FeTileLayout.renderCB(itemContent, w);
      //    // restore any sub-layout back
      //    if (n.subLayout?.el)
      //    {
      //      itemContent.appendChild(n.subLayout.el);
      //      if (!n.subLayout.opts.styleInHead)
      //        n.subLayout._updateStyles(true); // force create
      //    }
      //  }
      //  //delete w.content;
      //}

      // any remaining fields are assigned, but check for dragging changes, resize constrain
      let changed = false;
      let ddChanged = false;
      for (const key in w)
      {
        if (key[ 0 ] !== '_' && n[ key ] !== w[ key ])
        {
          n[ key ] = w[ key ];
          changed = true;
          ddChanged = ddChanged || (!this.opts.staticLayout && (key === 'noResize' || key === 'noMove' || key === 'locked'));
        }
      }
      Utils.sanitizeMinMax(n);

      // finally move the item and update attr
      if (m)
      {
        const widthChanged = (m.w !== undefined && m.w !== n.w);
        this.moveNode(n, m);
        this.resizeToContentCheck(widthChanged, n); // wait for animation if we changed width
        delete n._originalPosition; // clear out original position now that we moved #2669
      }

      if (m || changed)
      {
        this._writeAttr(el, n);
        console.log(`tileLayout.ts Update item -`, el, n);
      }

      if (ddChanged)
      {
        this._prepareDragDropByNode(n);
      }
    });

    return this;
  }

  private moveNode (n: Node, m: MoveOptions)
  {
    const wasUpdating = n._updating;
    if (!wasUpdating)
      this.engine.cleanNodes().beginUpdate(n);

    this.engine.moveNode(n, m);
    this._updateContainerHeight();
    if (!wasUpdating)
    {
      this._triggerChangeEvent();
      this.engine.endUpdate();
    }
  }

  /**
   * Updates item height to match the content height to avoid v-scrollbar or dead space.
   * Note: this assumes only 1 child under resizeToContentParent='.fe-tile-layout-item-content' (sized to layoutItem minus padding) that is at the entire content size wanted.
   * @param el tile layout item element
   * @param useNodeH set to true if tileLayoutNode.h should be used instead of actual container height when we don't need to wait for animation to finish to get actual DOM heights
   */
  public resizeToContent (el: ItemHTMLElement)
  {
    if (!el)
      return;

    el.classList.remove('size-to-content-max');
    if (!el.clientHeight)
      return; // 0 when hidden, skip

    const n = el.tileLayoutNode;
    if (!n)
      return;

    const tileLayout = n.tileLayout;
    if (!tileLayout || el.parentElement !== tileLayout.el)
      return; // skip if we are not inside a layout

    const cell = tileLayout.getCellHeight(true);
    if (!cell)
      return;

    let height = n.h ? n.h * cell : el.clientHeight; // getBoundingClientRect().height seem to flicker back and forth
    let item: Element;
    if (n.resizeToContentParent)
      item = el.querySelector(n.resizeToContentParent);

    if (!item)
      item = el.querySelector(FeTileLayout.resizeToContentParent);

    if (!item)
      return;

    const padding = el.clientHeight - item.clientHeight; // full - available height to our child (minus border, padding...)
    const itemH = n.h ? n.h * cell - padding : item.clientHeight; // calculated to what cellHeight is or will become (rather than actual to prevent waiting for animation to finish)
    let wantedH: number;
    if (n.subLayout)
    {
      // sub-layout - use their actual row count * their cell height
      wantedH = n.subLayout.getRow() * n.subLayout.getCellHeight(true);
    } else if (n.subLayoutOpts?.children?.length)
    {
      // not sub-layout just yet (case above) wait until we do
      return;
    } else
    {
      // NOTE: clientHeight & getBoundingClientRect() is undefined for text and other leaf nodes. use <div> container!
      const child = item.firstElementChild;
      if (!child)
      {
        console.error(`Error: FeTileLayout.resizeToContent() item id:${ n.id } '${ FeTileLayout.resizeToContentParent }'.firstElementChild is null, make sure to have a div like container. Skipping sizing.`);
        return;
      }
      wantedH = child.getBoundingClientRect().height || itemH;
    }

    if (itemH === wantedH) 
      return;

    height += wantedH - itemH;
    let h = Math.ceil(height / cell);
    // check for min/max and special sizing
    const softMax = Number.isInteger(n.sizeToContent) ? n.sizeToContent as number : 0;
    if (softMax && h > softMax)
    {
      h = softMax;
      el.classList.add('size-to-content-max');  // get v-scroll back
    }

    if (n.minH && h < n.minH) 
      h = n.minH;
    else if (n.maxH && h > n.maxH) 
      h = n.maxH;

    if (h !== n.h)
    {
      tileLayout._ignoreLayoutsNodeChange = true;
      tileLayout.moveNode(n, { h });
      delete tileLayout._ignoreLayoutsNodeChange;
    }
  }

  /** call the user resize (so they can do extra work) else our build in version */
  private resizeToContentCBCheck (el: ItemHTMLElement)
  {
    if (FeTileLayout.resizeToContentCB) FeTileLayout.resizeToContentCB(el);
    else this.resizeToContent(el);
  }

  /** rotate (by swapping w & h) the passed in node - called when user press 'r' during dragging
   * @param els  item or selector of objects to modify
   * @param relative optional pixel coord relative to upper/left corner to rotate around (will keep that cell under cursor)
   */
  public rotate (els: FeTileLayoutElement, relative?: Position): FeTileLayout
  {
    FeTileLayout.getElements(els).forEach(el =>
    {
      let n = el.tileLayoutNode;
      if (!Utils.canBeRotated(n)) 
        return;

      const rot: FeTileLayoutItem = { w: n.h, h: n.w, minH: n.minW, minW: n.minH, maxH: n.maxW, maxW: n.maxH };
      // if given an offset, adjust x/y by column/row bounds when user presses 'r' during dragging
      if (relative)
      {
        let pivotX = relative.left > 0 ? Math.floor(relative.left / this.cellWidth()) : 0;
        let pivotY = relative.top > 0 ? Math.floor(relative.top / (this.opts.cellHeight as number)) : 0;
        rot.x = n.x + pivotX - (n.h - (pivotY + 1));
        rot.y = (n.y + pivotY) - pivotX;
      }
      Object.keys(rot).forEach(k => { if (rot[ k ] === undefined) delete rot[ k ]; });
      const _orig = n._originalPosition;
      this.update(el, rot);
      n._originalPosition = _orig; // restore as move() will delete it
    });
    return this;
  }

  /**
   * Updates the margins which will set all 4 sides at once - see `FeTileLayoutOptions.margin` for format options (CSS string format of 1,2,4 values or single number).
   * @param value margin value
   */
  public margin (value: NumberOrString): FeTileLayout
  {
    let isMultiValue = (typeof value === 'string' && value.split(' ').length > 1);
    // check if we can skip re-creating our CSS file... won't check if multi values (too much hassle)
    if (!isMultiValue)
    {
      let data = Utils.parseHeight(value);
      if (this.opts.marginUnit === data.unit && this.opts.margin === data.h) 
        return;
    }
    // re-use existing margin handling
    this.opts.margin = value;
    this.opts.marginTop = this.opts.marginBottom = this.opts.marginLeft = this.opts.marginRight = undefined;
    this._initMargin();

    this._updateStyles(true); // true = force re-create

    return this;
  }

  /** returns current margin number value (undefined if 4 sides don't match) */
  public getMargin (): number { 
    return this.opts.margin as number; 
  }

  /**
   * Returns true if the height of the tile layout will be less than the vertical
   * constraint. Always returns true if tile layout doesn't have height constraint.
   * @param node contains x,y,w,h,auto-position options
   *
   * @example
   * if (layout.willItFit(newItem)) {
   *   layout.addItem(newItem);
   * } else {
   *   alert('Not enough free space to place the item');
   * }
   */
  public willItFit (node: FeTileLayoutItem): boolean
  {
    return this.engine.willItFit(node);
  }

  /** @internal */
  protected _triggerChangeEvent (): FeTileLayout
  {
    if (this.engine.batchMode)
      return this;

    let elements = this.engine.getDirtyNodes(true); // verify they really changed
    if (elements && elements.length)
    {
      if (!this._ignoreLayoutsNodeChange)
      {
        this.engine.layoutsNodesChange(elements);
      }
      this._triggerEvent('change', elements);
    }
    this.engine.saveInitial(); // we called, now reset initial values & dirty flags
    return this;
  }

  /** @internal */
  protected _triggerAddEvent (): FeTileLayout
  {
    if (this.engine.batchMode) 
      return this;

    if (this.engine.addedNodes?.length)
    {
      if (!this._ignoreLayoutsNodeChange)
      {
        this.engine.layoutsNodesChange(this.engine.addedNodes);
      }
      // prevent added nodes from also triggering 'change' event (which is called next)
      this.engine.addedNodes.forEach(n => { delete n._dirty; });
      const addedNodes = [ ...this.engine.addedNodes ];
      this.engine.addedNodes = [];
      this._triggerEvent('added', addedNodes);
    }
    return this;
  }

  /** @internal */
  public _triggerRemoveEvent (): FeTileLayout
  {
    if (this.engine.batchMode) 
      return this;

    if (this.engine.removedNodes?.length)
    {
      const removedNodes = [ ...this.engine.removedNodes ];
      this.engine.removedNodes = [];
      this._triggerEvent('removed', removedNodes);
    }
    return this;
  }

  /** @internal */
  protected _triggerEvent (type: string, data?: Node[]): FeTileLayout
  {
    let event = data ? new CustomEvent(type, { bubbles: false, detail: data }) : new Event(type);
    this.el.dispatchEvent(event);
    return this;
  }

  /** @internal called to delete the current dynamic style sheet used for our layout */
  protected _removeStylesheet (): FeTileLayout
  {
    if (this._styles)
    {
      const styleLocation = this.opts.styleInHead ? undefined : this.el.parentNode as HTMLElement;
      Utils.removeStylesheet(this._styleSheetClass, styleLocation);
      delete this._styles;
    }
    return this;
  }

  /** @internal updated/create the CSS styles for row based layout and initial margin setting */
  protected _updateStyles (forceUpdate = false, maxH?: number): FeTileLayout
  {
    // call to delete existing one if we change cellHeight / margin
    if (forceUpdate)
    {
      this._removeStylesheet();
    }

    if (maxH === undefined) 
      maxH = this.getRow();

    this._updateContainerHeight();

    // if user is telling us they will handle the CSS themselves by setting heights to 0. Do we need this opts really ??
    if (this.opts.cellHeight === 0)
    {
      return this;
    }

    let cellHeight = this.opts.cellHeight as number;
    let cellHeightUnit = this.opts.cellHeightUnit;
    let prefix = `.${ this._styleSheetClass } > .${ this.opts.itemClass }`;

    // create one as needed
    if (!this._styles)
    {
      // insert style to parent (instead of 'head' by default) to support WebComponent
      const styleLocation = this.opts.styleInHead ? undefined : this.el.parentNode as HTMLElement;
      this._styles = Utils.createStylesheet(this._styleSheetClass, styleLocation, {
        nonce: this.opts.nonce,
      });

      if (!this._styles) 
        return this;

      this._styles._max = 0;

      // these are done once only
      Utils.addCSSRule(this._styles, prefix, `height: ${ cellHeight }${ cellHeightUnit }`);
      // content margins
      let top: string = this.opts.marginTop + this.opts.marginUnit;
      let bottom: string = this.opts.marginBottom + this.opts.marginUnit;
      let right: string = this.opts.marginRight + this.opts.marginUnit;
      let left: string = this.opts.marginLeft + this.opts.marginUnit;
      let content = `${ prefix } > .fe-tile-layout-item-content`;
      let placeholder = `.${ this._styleSheetClass } > .fe-tile-layout-placeholder > .placeholder-content`;
      Utils.addCSSRule(this._styles, content, `top: ${ top }; right: ${ right }; bottom: ${ bottom }; left: ${ left };`);
      Utils.addCSSRule(this._styles, placeholder, `top: ${ top }; right: ${ right }; bottom: ${ bottom }; left: ${ left };`);
      // resize handles offset (to match margin)
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-n`, `top: ${ top };`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-s`, `bottom: ${ bottom }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-ne`, `right: ${ right }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-e`, `right: ${ right }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-se`, `right: ${ right }; bottom: ${ bottom }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-nw`, `left: ${ left }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-w`, `left: ${ left }`);
      Utils.addCSSRule(this._styles, `${ prefix } > .ui-resizable-sw`, `left: ${ left }; bottom: ${ bottom }`);
    }

    // now update the height specific fields
    maxH = maxH || this._styles._max;
    if (maxH > this._styles._max)
    {
      let getHeight = (rows: number): string => (cellHeight * rows) + cellHeightUnit;
      for (let i = this._styles._max + 1; i <= maxH; i++)
      {
        // start at 1
        Utils.addCSSRule(this._styles, `${ prefix }[fe-tl-y="${ i }"]`, `top: ${ getHeight(i) }`);
        // start at 2
        Utils.addCSSRule(this._styles, `${ prefix }[fe-tl-h="${ i + 1 }"]`, `height: ${ getHeight(i + 1) }`);
      }
      this._styles._max = maxH;
    }
    return this;
  }

  /** @internal */
  protected _updateContainerHeight (): FeTileLayout
  {
    if (!this.engine || this.engine.batchMode)
      return this;

    const parent = this.parentFeTileLayoutItem;
    let row = this.getRow() + this._extraDragRow; // this checks for minRow already
    const cellHeight = this.opts.cellHeight as number;

    const unit = this.opts.cellHeightUnit;
    if (!cellHeight)
      return this;

    // check for css min height (non nested layout). 
    // TODO: support mismatch, say: min % while unit is px.
    if (!parent)
    {
      const cssMinHeight = Utils.parseHeight(getComputedStyle(this.el)[ 'minHeight' ]);
      if (cssMinHeight.h > 0 && cssMinHeight.unit === unit)
      {
        const minRow = Math.floor(cssMinHeight.h / cellHeight);
        if (row < minRow)
        {
          row = minRow;
        }
      }
    }

    this.el.setAttribute('fe-tl-current-row', String(row));
    this.el.style.removeProperty('min-height');
    this.el.style.removeProperty('height');
    if (row)
    {
      // nested layouts have 'insert:0' to fill the space of parent by default, but we may be taller so use min-height for possible scrollbars
      this.el.style[ parent ? 'minHeight' : 'height' ] = row * cellHeight + unit;
    }

    // if we're a nested tile layout inside an sizeToContent item, tell it to resize itself too
    if (parent && !parent.tileLayout.engine.batchMode && Utils.shouldSizeToContent(parent))
    {
      parent.tileLayout.resizeToContentCBCheck(parent.el);
    }

    return this;
  }

  /** @internal */
  protected _prepareElement (el: ItemHTMLElement, triggerAddEvent = false, node?: Node): FeTileLayout
  {
    node = node || this._readAttr(el);
    el.tileLayoutNode = node;
    node.el = el;
    node.tileLayout = this;
    node = this.engine.addNode(node, triggerAddEvent);

    // write the dom sizes and class
    this._writeAttr(el, node);
    console.log('_prepareElement', el, node);
    el.classList.add(feTileLayoutDefaults.itemClass, this.opts.itemClass);
    const sizeToContent = Utils.shouldSizeToContent(node);
    sizeToContent ? el.classList.add('size-to-content') : el.classList.remove('size-to-content');
    if (sizeToContent) this.resizeToContentCheck(false, node);

    this._prepareDragDropByNode(node);
    return this;
  }

  /** @internal call to write position x,y,w,h attributes back to element */
  protected _writePosAttr (el: HTMLElement, n: PositionDimention): FeTileLayout
  {
    if (n.x !== undefined && n.x !== null)
    {
      el.setAttribute('fe-tl-x', String(n.x));
    }

    if (n.y !== undefined && n.y !== null)
    {
      el.setAttribute('fe-tl-y', String(n.y));
    }

    if (n.w !== undefined && n.w !== null)
    {
      el.setAttribute('fe-tl-w', String(n.w));
    }

    if (n.h !== undefined && n.h !== null)
    {
      el.setAttribute('fe-tl-h', String(n.h));
    }

    return this;
  }

  /** @internal call to write any default attributes back to element */
  protected _writeAttr (el: HTMLElement, node: FeTileLayoutItem): FeTileLayout
  {
    if (!node)
      return this;

    this._writePosAttr(el, node);

    let attrs /*: FeTileLayoutItem but strings */ = { // remaining attributes
      autoPosition: 'fe-tl-auto-position',
      noResize: 'fe-tl-no-resize',
      noMove: 'fe-tl-no-move',
      locked: 'fe-tl-locked',
      id: 'fe-tl-id',
    };

    for (const key in attrs)
    {
      if (node[ key ])
      { // 0 is valid for x,y only but done above already and not in list anyway
        el.setAttribute(attrs[ key ], String(node[ key ]));
      } else
      {
        el.removeAttribute(attrs[ key ]);
      }
    }
    return this;
  }

  /** @internal call to read any default attributes from element */
  protected _readAttr (el: HTMLElement, clearDefaultAttr = true): FeTileLayoutItem
  {
    let n: Node = {};
    n.x = Utils.toNumber(el.getAttribute('fe-tl-x'));
    n.y = Utils.toNumber(el.getAttribute('fe-tl-y'));
    n.w = Utils.toNumber(el.getAttribute('fe-tl-w'));
    n.h = Utils.toNumber(el.getAttribute('fe-tl-h'));
    n.autoPosition = Utils.toBool(el.getAttribute('fe-tl-auto-position'));
    n.noResize = Utils.toBool(el.getAttribute('fe-tl-no-resize'));
    n.noMove = Utils.toBool(el.getAttribute('fe-tl-no-move'));
    n.locked = Utils.toBool(el.getAttribute('fe-tl-locked'));
    n.id = el.getAttribute('fe-tl-id');

    // read but never written out
    n.maxW = Utils.toNumber(el.getAttribute('fe-tl-max-w'));
    n.minW = Utils.toNumber(el.getAttribute('fe-tl-min-w'));
    n.maxH = Utils.toNumber(el.getAttribute('fe-tl-max-h'));
    n.minH = Utils.toNumber(el.getAttribute('fe-tl-min-h'));

    // optimization to reduce un-needed attr that don't render or are default CSS
    if (clearDefaultAttr)
    {
      if (n.w === 1)
        el.removeAttribute('fe-tl-w');

      //if (n.h === 1)
      //  el.removeAttribute('fe-tl-h');

      if (n.maxW)
        el.removeAttribute('fe-tl-max-w');

      if (n.minW)
        el.removeAttribute('fe-tl-min-w');

      if (n.maxH)
        el.removeAttribute('fe-tl-max-h');

      if (n.minH)
        el.removeAttribute('fe-tl-min-h');
    }

    // remove any key not found (null or false which is default)
    for (const key in n)
    {
      if (!n.hasOwnProperty(key))
        return;

      if (!n[ key ] && n[ key ] !== 0)
      {
        // 0 can be valid value (x,y only really)
        delete n[ key ];
      }
    }

    return n;
  }

  /** @internal */
  protected _setStaticClass (): FeTileLayout
  {
    let classes = [ 'fe-tile-layout-static' ];

    if (this.opts.staticLayout)
    {
      this.el.classList.add(...classes);
      this.el.setAttribute('fe-tl-static', 'true');
    } else
    {
      this.el.classList.remove(...classes);
      this.el.removeAttribute('fe-tl-static');

    }
    return this;
  }

  /**
   * called when we are being resized - check if the one Column Mode needs to be turned on/off
   * and remember the prev columns we used, or get our count from parent, as well as check for cellHeight==='auto' (square)
   * or `sizeToContent` layoutItem options.
   */
  public onResize (): FeTileLayout
  {
    if (!this.el?.clientWidth)
      return; // return if we're gone or no size yet (will get called again)

    if (this.prevWidth === this.el.clientWidth)
      return; // no-op

    this.prevWidth = this.el.clientWidth;

    console.log('onResize ', this.el.clientWidth);

    this.batchUpdate();

    // see if we're nested and take our column count from our parent....
    let columnChanged = false;
    if (this._autoColumn && this.parentFeTileLayoutItem)
    {
      if (this.opts.column !== this.parentFeTileLayoutItem.w)
      {
        this.column(this.parentFeTileLayoutItem.w, 'none');
        columnChanged = true;
      }
    } else
    {
      // else check for dynamic column
      columnChanged = this.checkDynamicColumn();
    }

    // make the cells content square again
    if (this._isAutoCellHeight)
      this.cellHeight();

    // update any nested layouts, or items size
    this.engine.nodes.forEach(n =>
    {
      if (n.subLayout)
        n.subLayout.onResize();
    });

    if (!this._skipInitialResize)
      this.resizeToContentCheck(columnChanged); // wait for anim of column changed (DOM reflow before we can size correctly)

    delete this._skipInitialResize;

    this.batchUpdate(false);

    return this;
  }

  /** resizes content for given node (or all) if shouldSizeToContent() is true */
  private resizeToContentCheck (delay = false, n: Node = undefined)
  {
    if (!this.engine)
      return; // we've been deleted in between!

    // update any layoutItem height with sizeToContent, but wait for DOM $animation_speed to settle if we changed column count
    // TODO: is there a way to know what the final (post animation) size of the content will be so we can animate the column width and height together rather than sequentially ?
    if (delay && this.hasAnimationCSS())
      return setTimeout(() => this.resizeToContentCheck(false, n), 300 + 10);

    if (n)
    {
      if (Utils.shouldSizeToContent(n))
        this.resizeToContentCBCheck(n.el);

    } else if (this.engine.nodes.some(n => Utils.shouldSizeToContent(n)))
    {
      const nodes = [ ...this.engine.nodes ]; // in case order changes while resizing one
      this.batchUpdate();
      nodes.forEach(n =>
      {
        if (Utils.shouldSizeToContent(n))
          this.resizeToContentCBCheck(n.el);
      });
      this.batchUpdate(false);
    }
    // call this regardless of shouldSizeToContent because item might need to stretch to take available space after a resize
    if (this._gsEventHandler[ 'resizecontent' ])
      this._gsEventHandler[ 'resizecontent' ](null, n ? [ n ] : this.engine.nodes);
  }

  /** add or remove the tile layout element size event handler */
  protected _updateResizeEvent (forceRemove = false): FeTileLayout
  {
    // only add event if we're not nested (parent will call us) and we're auto sizing cells or supporting dynamic column (i.e. doing work)
    // or supporting new sizeToContent option.
    const trackSize = !this.parentFeTileLayoutItem && (this._isAutoCellHeight || this.opts.sizeToContent || this.opts.columnOpts
      || this.engine.nodes.find(n => n.sizeToContent));

    if (!forceRemove && trackSize && !this.resizeObserver)
    {
      this._sizeThrottle = Utils.throttle(() => this.onResize(), this.opts.cellHeightThrottle);
      this.resizeObserver = new ResizeObserver(() => this._sizeThrottle());
      this.resizeObserver.observe(this.el);
      this._skipInitialResize = true; // makeItem will originally have called on startup
    } else if ((forceRemove || !trackSize) && this.resizeObserver)
    {
      this.resizeObserver.disconnect();
      delete this.resizeObserver;
      delete this._sizeThrottle;
    }

    return this;
  }

  /** @internal convert a potential selector into actual element */
  public static getElement (els: FeTileLayoutElement = '.fe-tile-layout-item'): ItemHTMLElement
  {
    return Utils.getElement(els);
  }

  /** @internal */
  public static getElements (els: FeTileLayoutElement = '.fe-tile-layout-item'): ItemHTMLElement[]
  {
    return Utils.getElements(els);
  }

  /** @internal */
  public static getLayoutElement (els: FeTileLayoutElement): FeTileLayoutHTMLElement
  {
    return FeTileLayout.getElement(els);
  }
  
  /** @internal */
  public static getLayoutElements (els: string): FeTileLayoutHTMLElement[]
  {
    return Utils.getElements(els);
  }

  /** @internal initialize margin top/bottom/left/right and units */
  protected _initMargin (): FeTileLayout
  {
    let data: HeightData;
    let margin = 0;

    // support passing multiple values like CSS (ex: '5px 10px 0 20px')
    let margins: string[] = [];
    if (typeof this.opts.margin === 'string')
    {
      margins = this.opts.margin.split(' ');
    }

    if (margins.length === 2)
    { // top/bot, left/right like CSS
      this.opts.marginTop = this.opts.marginBottom = margins[ 0 ];
      this.opts.marginLeft = this.opts.marginRight = margins[ 1 ];
    } else if (margins.length === 4)
    { // Clockwise like CSS
      this.opts.marginTop = margins[ 0 ];
      this.opts.marginRight = margins[ 1 ];
      this.opts.marginBottom = margins[ 2 ];
      this.opts.marginLeft = margins[ 3 ];
    } else
    {
      data = Utils.parseHeight(this.opts.margin);
      this.opts.marginUnit = data.unit;
      margin = this.opts.margin = data.h;
    }

    // see if top/bottom/left/right need to be set as well
    if (this.opts.marginTop === undefined)
    {
      this.opts.marginTop = margin;
    } else
    {
      data = Utils.parseHeight(this.opts.marginTop);
      this.opts.marginTop = data.h;
      delete this.opts.margin;
    }

    if (this.opts.marginBottom === undefined)
    {
      this.opts.marginBottom = margin;
    } else
    {
      data = Utils.parseHeight(this.opts.marginBottom);
      this.opts.marginBottom = data.h;
      delete this.opts.margin;
    }

    if (this.opts.marginRight === undefined)
    {
      this.opts.marginRight = margin;
    } else
    {
      data = Utils.parseHeight(this.opts.marginRight);
      this.opts.marginRight = data.h;
      delete this.opts.margin;
    }

    if (this.opts.marginLeft === undefined)
    {
      this.opts.marginLeft = margin;
    } else
    {
      data = Utils.parseHeight(this.opts.marginLeft);
      this.opts.marginLeft = data.h;
      delete this.opts.margin;
    }
    this.opts.marginUnit = data.unit; // in case side were spelled out, use those units instead...
    if (this.opts.marginTop === this.opts.marginBottom && this.opts.marginLeft === this.opts.marginRight && this.opts.marginTop === this.opts.marginRight)
    {
      this.opts.margin = this.opts.marginTop; // makes it easier to check for no-ops in setMargin()
    }
    return this;
  }

  /** get the global (but static to this code) DD implementation */
  public static getDD (): DragDropTileLayout
  {
    return dd;
  }

  /**
   * call to setup dragging in from the outside (say toolbar), by specifying the class selection and options.
   * Called during FeTileLayout.init() as options, but can also be called directly (last param are used) in case the toolbar
   * is dynamically create and needs to be set later.
   * @param dragIn string selector (ex: '.sidebar .fe-tile-layout-item') or list of dom elements
   * @param dragInOptions options - see DDDragInOpt. (default: {handle: '.fe-tile-layout-item-content', appendTo: 'body'}
   * @param root optional root which defaults to document (for shadow dom pas the parent HTMLDocument)
   */
  public static setupDragIn (dragIn?: string | HTMLElement[], dragInOptions?: DragDropDragInOptions, root: HTMLElement | Document = document): void
  {
    if (dragInOptions?.pause !== undefined)
    {
      DragDropManager.pauseDrag = dragInOptions.pause;
    }

    dragInOptions = { ...dragInDefaultOptions, ...(dragInOptions || {}) };
    let els: HTMLElement[] = (typeof dragIn === 'string') ? Utils.getElements(dragIn, root) : dragIn;
    if (els.length) els?.forEach(el =>
    {
      if (!dd.isDraggable(el)) dd.dragIn(el, dragInOptions);
    });
  }

  /**
   * Enables/Disables dragging by the user of specific tile layout element. If you want all items, and have it affect future items, use enableMove() instead. No-op for static layouts.
   * IF you are looking to prevent an item from moving (due to being pushed around by another during collision) use locked property instead.
   * @param els item or selector to modify.
   * @param val if true item will be draggable, assuming the parent tile layout isn't noMove or static.
   */
  public movable (els: FeTileLayoutElement, val: boolean): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return this; // can't move a static layout!

    FeTileLayout.getElements(els).forEach(el =>
    {
      const n = el.tileLayoutNode;
      if (!n) 
        return;

      val ? delete n.noMove : n.noMove = true;
      this._prepareDragDropByNode(n); // init DD if need be, and adjust
    });
    return this;
  }

  /**
   * Enables/Disables user resizing of specific tile layout element. If you want all items, and have it affect future items, use enableResize() instead. No-op for static layouts.
   * @param els  item or selector to modify
   * @param val  if true item will be resizable, assuming the parent tile layout isn't noResize or static.
   */
  public resizable (els: FeTileLayoutElement, val: boolean): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return this; // can't resize a static layout!

    FeTileLayout.getElements(els).forEach(el =>
    {
      let n = el.tileLayoutNode;
      if (!n)
        return;

      val ? delete n.noResize : n.noResize = true;
      this._prepareDragDropByNode(n); // init DD if need be, and adjust
    });
    return this;
  }

  /**
   * Temporarily disables items moving/resizing.
   * If you want a more permanent way (which freezes up resources) use `setStatic(true)` instead.
   * Note: no-op for static layout
   * This is a shortcut for:
   * @example
   *  layout.enableMove(false);
   *  layout.enableResize(false);
   * @param recurse true (default) if sub-layouts also get updated
   */
  public disable (recurse = true): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return;

    this.enableMove(false, recurse);
    this.enableResize(false, recurse);
    this._triggerEvent('disable');
    return this;
  }
  /**
   * Re-enables items moving/resizing - see disable().
   * Note: no-op for static layout.
   * This is a shortcut for:
   * @example
   *  layout.enableMove(true);
   *  layout.enableResize(true);
   * @param recurse true (default) if sub-layouts also get updated
   */
  public enable (recurse = true): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return;

    this.enableMove(true, recurse);
    this.enableResize(true, recurse);
    this._triggerEvent('enable');
    return this;
  }

  /**
   * Enables/disables item moving. No-op for static layouts, and locally defined items still overrule
   * @param recurse true (default) if sub-layouts also get updated
   */
  public enableMove (doEnable: boolean, recurse = true): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return this; // can't move a static layout!

    doEnable ? delete this.opts.disableDrag : this.opts.disableDrag = true; // FIRST before we update children as tile layout overrides #1658
    this.engine.nodes.forEach(n =>
    {
      this._prepareDragDropByNode(n);
      if (n.subLayout && recurse) 
        n.subLayout.enableMove(doEnable, recurse);
    });
    return this;
  }

  /**
   * Enables/disables item resizing. No-op for static layouts.
   * @param recurse true (default) if sub-layouts also get updated
   */
  public enableResize (doEnable: boolean, recurse = true): FeTileLayout
  {
    if (this.opts.staticLayout) 
      return this; // can't size a static layout!

    doEnable ? delete this.opts.disableResize : this.opts.disableResize = true; // FIRST before we update children as tile layout overrides #1658
    this.engine.nodes.forEach(n =>
    {
      this._prepareDragDropByNode(n);
      if (n.subLayout && recurse) 
        n.subLayout.enableResize(doEnable, recurse);
    });
    return this;
  }

  /** @internal removes any drag&drop present (called during destroy) */
  protected _removeDD (el: DragDropElementHost): FeTileLayout
  {
    dd.draggable(el, 'destroy').resizable(el, 'destroy');
    if (el.tileLayoutNode)
    {
      delete el.tileLayoutNode._initDD; // reset our DD init flag
    }
    delete el.ddElement;
    return this;
  }

  /** @internal called to add drag over to support items being added externally */
  protected _setupAcceptItem (): FeTileLayout
  {
    // check if we need to disable things
    if (this.opts.staticLayout || (!this.opts.acceptItems && !this.opts.removable))
    {
      dd.droppable(this.el, 'destroy');
      return this;
    }

    // vars shared across all methods
    let cellHeight: number, cellWidth: number;

    let onDrag = (event: DragEvent, el: ItemHTMLElement, helper: ItemHTMLElement) =>
    {
      let node = el.tileLayoutNode;
      if (!node) 
        return;

      helper = helper || el;

      // if the element is being dragged from outside, scale it down to match the layout's scale
      // and slightly adjust its position relative to the mouse
      if (!node.tileLayout?.el)
      {
        // this scales the helper down
        helper.style.transform = `scale(${ 1 / this.dragTransform.xScale },${ 1 / this.dragTransform.yScale })`;
        // this makes it so that the helper is well positioned relative to the mouse after scaling
        const helperRect = helper.getBoundingClientRect();
        helper.style.left = helperRect.x + (this.dragTransform.xScale - 1) * (event.clientX - helperRect.x) / this.dragTransform.xScale + 'px';
        helper.style.top = helperRect.y + (this.dragTransform.yScale - 1) * (event.clientY - helperRect.y) / this.dragTransform.yScale + 'px';
        helper.style.transformOrigin = `0px 0px`;
      }

      let parent = this.el.getBoundingClientRect();
      let { top, left } = helper.getBoundingClientRect();
      left -= parent.left;
      top -= parent.top;
      let ui: DragDropUIData = {
        position: {
          top: top * this.dragTransform.xScale,
          left: left * this.dragTransform.yScale
        }
      };

      if (node._temporaryRemoved)
      {
        node.x = Math.max(0, Math.round(left / cellWidth));
        node.y = Math.max(0, Math.round(top / cellHeight));
        delete node.autoPosition;
        this.engine.nodeBoundFix(node);

        // don't accept *initial* location if doesn't fit #1419 (locked drop region, or can't grow), but maybe try if it will go somewhere
        if (!this.engine.willItFit(node))
        {
          node.autoPosition = true; // ignore x,y and try for any slot...
          if (!this.engine.willItFit(node))
          {
            dd.off(el, 'drag'); // stop calling us
            return; // full tile layout or can't grow
          }
          if (node._willFitPosition)
          {
            // use the auto position instead #1687
            Utils.copyPos(node, node._willFitPosition);
            delete node._willFitPosition;
          }
        }

        // re-use the existing node dragging method
        this._onStartMoving(helper, event, ui, node, cellWidth, cellHeight);
      } else
      {
        // re-use the existing node dragging that does so much of the collision detection
        this._dragOrResize(helper, event, ui, node, cellWidth, cellHeight);
      }
    };

    dd.droppable(this.el, {
      accept: (el: ItemHTMLElement) =>
      {
        let node: Node = el.tileLayoutNode || this._readAttr(el, false);
        // set accept drop to true on ourself (which we ignore) so we don't get "can't drop" icon in HTML5 mode while moving
        if (node?.tileLayout === this) 
          return true;

        if (!this.opts.acceptItems) 
          return false;

        // check for accept method or class matching
        let canAccept = true;
        if (typeof this.opts.acceptItems === 'function')
        {
          canAccept = this.opts.acceptItems(el);
        } else
        {
          let selector = (this.opts.acceptItems === true ? '.fe-tile-layout-item' : this.opts.acceptItems as string);
          canAccept = el.matches(selector);
        }

        // finally check to make sure we actually have space left #1571 #2633
        if (canAccept && node && this.opts.maxRow)
        {
          let n = { w: node.w, h: node.h, minW: node.minW, minH: node.minH }; // only width/height matters and autoPosition
          canAccept = this.engine.willItFit(n);
        }
        return canAccept;
      }
    })
      /**
       * entering our tile layout area
       */
      .on(this.el, 'dropover', (event: Event, el: ItemHTMLElement, helper: ItemHTMLElement) =>
      {
        // console.log(`over ${this.el.feTileLayout.opts.id} ${count++}`); // TEST
        let node = el.tileLayoutNode;
        // ignore drop enter on ourself (unless we temporarily removed) which happens on a simple drag of our item
        if (node?.tileLayout === this && !node._temporaryRemoved)
        {
          // delete node._added; // reset this to track placeholder again in case we were over other tile layout #1484 (dropout doesn't always clear)
          return false; // prevent parent from receiving msg (which may be a tile layout as well)
        }

        // fix #1578 when dragging fast, we may not get a leave on the previous tile layout so force one now
        if (node?.tileLayout && node.tileLayout !== this && !node._temporaryRemoved)
        {
          // console.log('dropover without leave'); // TEST
          let otherLayout = node.tileLayout;
          otherLayout._leave(el, helper);
        }

        // cache cell dimensions (which don't change), position can animate if we removed an item in otherLayout that affects us...
        cellWidth = this.cellWidth();
        cellHeight = this.getCellHeight(true);

        // load any element attributes if we don't have a node
        if (!node)
        {
          node = this._readAttr(el, false); // don't wipe external (e.g. drag toolbar) attr #2354
        }

        if (!node.tileLayout)
        {
          node._isExternal = true;
          el.tileLayoutNode = node;
        }

        // calculate the tile layout size based on element outer size
        helper = helper || el;
        let w = node.w || Math.round(helper.offsetWidth / cellWidth) || 1;
        let h = node.h || Math.round(helper.offsetHeight / cellHeight) || 1;

        // if the item came from another layout, make a copy and save the original info in case we go back there
        if (node.tileLayout && node.tileLayout !== this)
        {
          // copy the node original values (min/max/id/etc...) but override width/height/other flags which are this tile layout specific
          // console.log('dropover cloning node'); // TEST
          if (!el._tileLayoutNodeOrig)
            el._tileLayoutNodeOrig = node; // shouldn't have multiple nested!

          el.tileLayoutNode = node = { ...node, w, h, tileLayout: this };
          delete node.x;
          delete node.y;
          this.engine.cleanupNode(node)
            .nodeBoundFix(node);
          // restore some internal fields we need after clearing them all
          node._initDD =
            node._isExternal =  // DOM needs to be re-parented on a drop
            node._temporaryRemoved = true; // so it can be inserted onDrag below
        } else
        {
          node.w = w; node.h = h;
          node._temporaryRemoved = true; // so we can insert it
        }

        // clear any marked for complete removal (Note: don't check _isAboutToRemove as that is cleared above - just do it)
        FeTileLayout._itemRemoving(node.el, false);

        dd.on(el, 'drag', onDrag);
        // make sure this is called at least once when going fast #1578
        onDrag(event as DragEvent, el, helper);
        return false; // prevent parent from receiving msg (which may be a tile layout as well)
      })
      /**
       * Leaving our tile layout area...
       */
      .on(this.el, 'dropout', (event, el: ItemHTMLElement, helper: ItemHTMLElement) =>
      {
        // console.log(`out ${this.el.tileLayout.opts.id} ${count++}`); // TEST
        let node = el.tileLayoutNode;
        if (!node) 
          return false;

        // fix #1578 when dragging fast, we might get leave after other tile layout gets enter (which calls us to clean)
        // so skip this one if we're not the active tile layout really..
        if (!node.tileLayout || node.tileLayout === this)
        {
          this._leave(el, helper);
          // if we were created as temporary nested layout, go back to before state
          if (this._isTemp)
          {
            this.removeAssubLayout(node);
          }
        }
        return false; // prevent parent from receiving msg (which may be tile layout as well)
      })
      /**
       * end - releasing the mouse
       */
      .on(this.el, 'drop', (event, el: ItemHTMLElement, helper: ItemHTMLElement) =>
      {
        let node = el.tileLayoutNode;
        // ignore drop on ourself from ourself that didn't come from the outside - dragend will handle the simple move instead
        if (node?.tileLayout === this && !node._isExternal) 
          return false;

        const wasAdded = !!this.placeholder.parentElement; // skip items not actually added to us because of constrains, but do cleanup #1419
        this.placeholder.remove();

        // disable animation when replacing a placeholder (already positioned) with actual content
        const noAnim = wasAdded && this.opts.animate;
        if (noAnim) this.setAnimation(false);

        // notify previous tile layout of removal
        let origNode = el._tileLayoutNodeOrig;
        delete el._tileLayoutNodeOrig;
        if (wasAdded && origNode?.tileLayout && origNode.tileLayout !== this)
        {
          let oTileLayout = origNode.tileLayout;
          oTileLayout.engine.removeNodeFromLayoutCache(origNode);
          oTileLayout.engine.removedNodes.push(origNode);
          oTileLayout._triggerRemoveEvent()._triggerChangeEvent();
          // if it's an empty sub-layout that got auto-created, nuke it
          if (oTileLayout.parentFeTileLayoutItem && !oTileLayout.engine.nodes.length && oTileLayout.opts.subLayoutDynamic)
          {
            oTileLayout.removeAssubLayout();
          }
        }

        if (!node) 
          return false;

        // use existing placeholder node as it's already in our list with drop location
        if (wasAdded)
        {
          this.engine.cleanupNode(node); // removes all internal _xyz values
          node.tileLayout = this;
        }
        delete node.tileLayout?._isTemp;
        dd.off(el, 'drag');

        // if we made a copy ('helper' which is temp) of the original node then insert a copy, else we move the original node (#1102)
        // as the helper will be nuked by jquery-ui otherwise. TODO: update old code path
        if (helper !== el)
        {
          helper.remove();
          el.tileLayoutNode = origNode; // original item (left behind) is re-stored to pre dragging as the node now has drop info
          if (wasAdded)
          {
            el = el.cloneNode(true) as ItemHTMLElement;
          }
        } else
        {
          el.remove(); // reduce flicker as we change depth here, and size further down
          this._removeDD(el);
        }

        if (!wasAdded) 
          return false;

        el.tileLayoutNode = node;
        node.el = el;
        let subLayout = node.subLayout?.el?.tileLayout; // set when actual sub-layout present
        // @ts-ignore
        Utils.copyPos(node, this._readAttr(this.placeholder)); // placeholder values as moving VERY fast can throw things off #1578
        Utils.removePositioningStyles(el);// @ts-ignore
        this.el.appendChild(el);// @ts-ignore // TODO: now would be ideal time to _removeHelperStyle() overriding floating styles (native only)
        this._prepareElement(el, true, node);
        if (subLayout)
        {
          subLayout.parentFeTileLayoutItem = node;
          if (!subLayout.opts.styleInHead) 
            subLayout._updateStyles(true); // re-create sub-layout styles now that we've moved
        }
        this._updateContainerHeight();
        this.engine.addedNodes.push(node);// @ts-ignore
        this._triggerAddEvent();// @ts-ignore
        this._triggerChangeEvent();

        this.engine.endUpdate();
        if (this._gsEventHandler[ 'dropped' ])
        {
          this._gsEventHandler[ 'dropped' ]({ ...event, type: 'dropped' }, origNode && origNode.tileLayout ? origNode : undefined, node);
        }

        // delay adding animation back
        if (noAnim) 
          this.setAnimation(this.opts.animate, true);

        return false; // prevent parent from receiving msg (which may be tile layout as well)
      });
    return this;
  }

  /** @internal mark item for removal */
  private static _itemRemoving (el: ItemHTMLElement, remove: boolean)
  {
    const node = el ? el.tileLayoutNode : undefined;
    if (!node?.tileLayout || el.classList.contains(node.tileLayout.opts.removableOptions.decline)) 
      return;

    remove ? node._isAboutToRemove = true : delete node._isAboutToRemove;
    remove ? el.classList.add('fe-tile-layout-item-removing') : el.classList.remove('fe-tile-layout-item-removing');
  }

  /** @internal called to setup a trash drop zone if the user specifies it */
  protected _setupRemoveDrop (): FeTileLayout
  {
    if (typeof this.opts.removable !== 'string') 
      return this;

    let trashEl = document.querySelector(this.opts.removable) as HTMLElement;
    if (!trashEl) 
      return this;

    // only register ONE static drop-over/dropout callback for the 'trash', and it will
    // update the passed in item and parent tile layout because the '.trash' is a shared resource anyway,
    // and Native DD only has 1 event CB (having a list and technically a per tile layout removableOptions complicates things greatly)
    if (!this.opts.staticLayout && !dd.isDroppable(trashEl))
    {
      dd.droppable(trashEl, this.opts.removableOptions)
        .on(trashEl, 'dropover', (event, el) => FeTileLayout._itemRemoving(el, true))
        .on(trashEl, 'dropout', (event, el) => FeTileLayout._itemRemoving(el, false));
    }
    return this;
  }

  /** @internal prepares the element for drag&drop */
  protected _prepareDragDropByNode (node: Node): FeTileLayout
  {
    let el = node.el;
    const noMove = node.noMove || this.opts.disableDrag;
    const noResize = node.noResize || this.opts.disableResize;

    // check for disabled tile layout first
    if (this.opts.staticLayout || (noMove && noResize))
    {
      if (node._initDD)
      {
        this._removeDD(el); // nukes everything instead of just disable, will add some styles back next
        delete node._initDD;
      }
      el.classList.add('ui-draggable-disabled', 'ui-resizable-disabled'); // add styles one might depend on #1435
      return this;
    }

    if (!node._initDD)
    {
      // variables used/cashed between the 3 start/move/end methods, in addition to node passed above
      let cellWidth: number;
      let cellHeight: number;

      /** called when item starts moving/resizing */
      let onStartMoving = (event: Event, ui: DragDropUIData) =>
      {
        // trigger any 'dragstart' / 'resizestart' manually
        if (this._gsEventHandler[ event.type ])
        {
          this._gsEventHandler[ event.type ](event, event.target);
        }
        cellWidth = this.cellWidth();
        cellHeight = this.getCellHeight(true); // force pixels for calculations

        this._onStartMoving(el, event, ui, node, cellWidth, cellHeight);
      };

      /** called when item is being dragged/resized */
      let dragOrResize = (event: MouseEvent, ui: DragDropUIData) =>
      {
        this._dragOrResize(el, event, ui, node, cellWidth, cellHeight);
      };

      /** called when the item stops moving/resizing */
      let onEndMoving = (event: Event) =>
      {
        this.placeholder.remove();
        delete node._moving;
        delete node._event;
        delete node._lastTried;
        const widthChanged = node.w !== node._originalPosition.w;

        // if the item has moved to another layout, we're done here
        let target: ItemHTMLElement = event.target as ItemHTMLElement;
        if (!target.tileLayoutNode || target.tileLayoutNode.tileLayout !== this) 
          return;

        node.el = target;

        if (node._isAboutToRemove)
        {
          let tileLayout = el.tileLayoutNode.tileLayout;
          if (tileLayout._gsEventHandler[ event.type ])
          {
            tileLayout._gsEventHandler[ event.type ](event, target);
          }
          tileLayout.engine.nodes.push(node); // temp add it back so we can proper remove it next
          tileLayout.removeItem(el, true, true);
        } else
        {
          Utils.removePositioningStyles(target);
          if (node._temporaryRemoved)
          {
            // got removed - restore item back to before dragging position
            Utils.copyPos(node, node._originalPosition);// @ts-ignore
            this._writePosAttr(target, node);
            console.log('onEndMoving _temporaryRemoved', target);
            this.engine.addNode(node);
          } else
          {
            // move to new placeholder location
            this._writePosAttr(target, node);
            console.log('onEndMoving not_temporaryRemoved', target);
          }

          if (this._gsEventHandler[ event.type ])
          {
            console.log('onEndMoving _gsEventHandler', event.type, target);
            this._gsEventHandler[ event.type ](event, target);
          }
        }
        // @ts-ignore
        this._extraDragRow = 0;// @ts-ignore
        this._updateContainerHeight();// @ts-ignore
        this._triggerChangeEvent();

        this.engine.endUpdate();

        if (event.type === 'resizestop')
        {
          console.info('resizestop a', node.sizeToContent, node.h, node._originalPosition.h,
            node.sizeToContent);
          if (Number.isInteger(node.sizeToContent))
            node.sizeToContent = node.h; // new soft limit

          console.info('resizestop b', node.sizeToContent, node.h, node._originalPosition.h,
            node.sizeToContent);
          this.resizeToContentCheck(widthChanged, node); // wait for width animation if changed
        }
      };

      dd.draggable(el, {
        start: onStartMoving,
        stop: onEndMoving,
        drag: dragOrResize
      }).resizable(el, {
        start: onStartMoving,
        stop: onEndMoving,
        resize: dragOrResize
      });
      node._initDD = true; // we've set DD support now
    }

    // finally fine tune move vs resize by disabling any part...
    dd.draggable(el, noMove ? 'disable' : 'enable')
      .resizable(el, noResize ? 'disable' : 'enable');

    return this;
  }

  /** @internal handles actual drag/resize start */
  protected _onStartMoving (el: ItemHTMLElement, event: Event, ui: DragDropUIData, node: Node, cellWidth: number, cellHeight: number): void
  {
    this.engine.cleanNodes()
      .beginUpdate(node);
    // @ts-ignore
    this._writePosAttr(this.placeholder, node);
    console.log('_onStartMoving', node, this.placeholder);
    this.el.appendChild(this.placeholder);
    this.placeholder.tileLayoutNode = node;
    // console.log('_onStartMoving placeholder') // TEST

    // if the element is inside a layout, it has already been scaled
    // we can use that as a scale reference
    if (node.tileLayout?.el)
    {
      this.dragTransform = Utils.getValuesFromTransformedElement(el);
    }
    // if the element is being dragged from outside (not from any layout)
    // we use the tile layout as the transformation reference, since the helper is not subject to transformation
    else if (this.placeholder && this.placeholder.closest('.fe-tile-layout'))
    {
      const layoutEl = this.placeholder.closest('.fe-tile-layout') as HTMLElement;
      this.dragTransform = Utils.getValuesFromTransformedElement(layoutEl);
    }
    // Fallback
    else
    {
      this.dragTransform = {
        xScale: 1,
        xOffset: 0,
        yScale: 1,
        yOffset: 0,
      };
    }

    node.el = this.placeholder;
    node._lastUiPosition = ui.position;
    node._previousYPixels = ui.position.top;
    node._moving = (event.type === 'dragstart'); // 'dropover' are not initially moving so they can go exactly where they enter (will push stuff out of the way)
    delete node._lastTried;

    if (event.type === 'dropover' && node._temporaryRemoved)
    {
      // console.log('engine.addNode x=' + node.x); // TEST
      this.engine.addNode(node); // will add, fix collisions, update attr and clear _temporaryRemoved
      node._moving = true; // AFTER, mark as moving object (wanted fix location before)
    }

    // set the min/max resize info taking into account the column count and position (so we don't resize outside the layout)
    this.engine.cacheRects(cellWidth, cellHeight, this.opts.marginTop as number, this.opts.marginRight as number, this.opts.marginBottom as number, this.opts.marginLeft as number);
    if (event.type === 'resizestart')
    {
      const colLeft = this.getColumn() - node.x;
      const rowLeft = (this.opts.maxRow || Number.MAX_SAFE_INTEGER) - node.y;
      dd.resizable(el, 'option', 'minWidth', cellWidth * Math.min(node.minW || 1, colLeft))
        .resizable(el, 'option', 'minHeight', cellHeight * Math.min(node.minH || 1, rowLeft))
        .resizable(el, 'option', 'maxWidth', cellWidth * Math.min(node.maxW || Number.MAX_SAFE_INTEGER, colLeft))
        .resizable(el, 'option', 'maxWidthMoveLeft', cellWidth * Math.min(node.maxW || Number.MAX_SAFE_INTEGER, node.x + node.w))
        .resizable(el, 'option', 'maxHeight', cellHeight * Math.min(node.maxH || Number.MAX_SAFE_INTEGER, rowLeft))
        .resizable(el, 'option', 'maxHeightMoveUp', cellHeight * Math.min(node.maxH || Number.MAX_SAFE_INTEGER, node.y + node.h));
    }
  }

  /** @internal handles actual drag/resize */
  protected _dragOrResize (el: ItemHTMLElement, event: MouseEvent, ui: DragDropUIData, node: Node, cellWidth: number, cellHeight: number): void
  {
    console.log('dragOrResize', event.type, node._temporaryRemoved,
      node._isExternal, node._moving, cellHeight, cellWidth,
      this.opts.marginLeft, this.opts.marginRight, this.opts.marginTop,
      this.opts.marginBottom);

    let p = { ...node._originalPosition }; // could be undefined (_isExternal) which is ok (drag only set x,y and w,h will default to node value)
    let resizing: boolean;
    let mLeft = this.opts.marginLeft as number;
    let mRight = this.opts.marginRight as number;
    let mTop = this.opts.marginTop as number;
    let mBottom = this.opts.marginBottom as number;

    // if margins (which are used to pass mid point by) are large relative to cell height/width, reduce them down #1855
    let mHeight = Math.round(cellHeight * 0.1);
    let mWidth = Math.round(cellWidth * 0.1);
    mLeft = Math.min(mLeft, mWidth);
    mRight = Math.min(mRight, mWidth);
    mTop = Math.min(mTop, mHeight);
    mBottom = Math.min(mBottom, mHeight);

    if (event.type === 'drag')
    {
      if (node._temporaryRemoved)
        return; // handled by dropover

      let distance = ui.position.top - node._previousYPixels;
      node._previousYPixels = ui.position.top;
      if (this.opts.draggable.scroll !== false)
      {
        Utils.updateScrollPosition(el, ui.position, distance);
      }

      // get new position taking into account the margin in the direction we are moving! (need to pass mid point by margin)
      let left = ui.position.left + (ui.position.left > node._lastUiPosition.left ? -mRight : mLeft);
      let top = ui.position.top + (ui.position.top > node._lastUiPosition.top ? -mBottom : mTop);
      p.x = Math.round(left / cellWidth);
      p.y = Math.round(top / cellHeight);

      // @ts-ignore// if we're at the bottom hitting something else, grow the tile layout so cursor doesn't leave when trying to place below others
      let prev = this._extraDragRow;
      if (this.engine.collide(node, p))
      {
        let row = this.getRow();
        let extra = Math.max(0, (p.y + node.h) - row);
        if (this.opts.maxRow && row + extra > this.opts.maxRow)
        {
          extra = Math.max(0, this.opts.maxRow - row);
        }// @ts-ignore
        this._extraDragRow = extra;// @ts-ignore
      } else 
      {
        this._extraDragRow = 0;// @ts-ignore
      }

      if (this._extraDragRow !== prev)
        this._updateContainerHeight();

      if (node.x === p.x && node.y === p.y)
        return; // skip same
      // DON'T skip one we tried as we might have failed because of coverage <50% before
      // if (node._lastTried && node._lastTried.x === x && node._lastTried.y === y) return;
    } else if (event.type === 'resize')
    {
      if (p.x < 0)
        return;

      // Scrolling page if needed
      Utils.updateScrollResize(event, el, cellHeight);

      // get new size
      p.w = Math.round((ui.size.width - mLeft) / cellWidth);
      p.h = Math.round((ui.size.height - mTop) / cellHeight);
      if (node.w === p.w && node.h === p.h)
        return;

      if (node._lastTried && node._lastTried.w === p.w && node._lastTried.h === p.h)
        return; // skip one we tried (but failed)

      // if we size on left/top side this might move us, so get possible new position as well
      let left = ui.position.left + mLeft;
      let top = ui.position.top + mTop;
      p.x = Math.round(left / cellWidth);
      p.y = Math.round(top / cellHeight);

      resizing = true;
    }

    node._event = event;
    node._lastTried = p; // set as last tried (will nuke if we go there)
    let rect: PositionDimention = { // screen pix of the dragged box
      x: ui.position.left + mLeft,
      y: ui.position.top + mTop,
      w: (ui.size ? ui.size.width : node.w * cellWidth) - mLeft - mRight,
      h: (ui.size ? ui.size.height : node.h * cellHeight) - mTop - mBottom
    };

    console.log('dragOrResize', rect, p, resizing);

    if (this.engine.moveNodeCheck(node, { ...p, cellWidth, cellHeight, rect, resizing }))
    {
      node._lastUiPosition = ui.position;
      this.engine.cacheRects(cellWidth, cellHeight, mTop, mRight, mBottom, mLeft);
      delete node._skipDown;
      if (resizing && node.subLayout)
        node.subLayout.onResize();

      this._extraDragRow = 0;// @ts-ignore
      this._updateContainerHeight();

      let target = event.target as ItemHTMLElement;// @ts-ignore
      this._writePosAttr(target, node);
      console.log('dragOrResize _writePosAttr', target, node);
      console.log('dragOrResize _writePosAttr',
        target.getAttribute('fe-tl-h'),
        target.getAttribute('fe-tl-w'),
        target.getAttribute('fe-tl-x'),
        target.getAttribute('fe-tl-y')
        , node.x, node.y, node.w, node.h);
      if (this._gsEventHandler[ event.type ])
      {
        console.log('dragOrResize _gsEventHandler', event.type, target);
        console.log('dragOrResize XXXXXXXXXX',
          target.getAttribute('fe-tl-h'),
          target.getAttribute('fe-tl-w'),
          node.h, node.w);
        this._gsEventHandler[ event.type ](event, target);
      }
    }
  }

  /** @internal called when item leaving our area by either cursor dropout event
   * or shape is outside our boundaries. remove it from us, and mark temporary if this was
   * our item to start with else restore prev node values from prev tile layout it came from.
   */
  protected _leave (el: ItemHTMLElement, helper?: ItemHTMLElement): void
  {
    let node = el.tileLayoutNode;
    if (!node)
      return;

    helper = helper || el;
    // restore the scale of the helper on leave
    helper.style.transform = 'scale(1)';
    dd.off(el, 'drag'); // no need to track while being outside

    // this gets called when cursor leaves and shape is outside, so only do this once
    if (node._temporaryRemoved)
      return;

    node._temporaryRemoved = true;

    this.engine.removeNode(node); // remove placeholder as well, otherwise it's a sign node is not in our list, which is a bigger issue
    node.el = node._isExternal && helper ? helper : el; // point back to real item being dragged

    if (this.opts.removable === true)
    { // boolean vs a class string
      // item leaving us and we are supposed to remove on leave (no need to drag onto trash) mark it so
      FeTileLayout._itemRemoving(el, true);
    }

    // finally if item originally came from another layout, but left us, restore things back to prev info
    if (el._tileLayoutNodeOrig)
    {
      el.tileLayoutNode = el._tileLayoutNodeOrig;
      delete el._tileLayoutNodeOrig;
    } else if (node._isExternal)
    {
      // item came from outside (like a toolbar) so nuke any node info
      delete node.el;
      delete el.tileLayoutNode;
      // and restore all nodes back to original
      this.engine.restoreInitial();
    }
  }
}
